import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus, Trash2, Loader2, Send, ArrowLeft, Edit, Archive,
  Globe, FileText, Search, MoreVertical, Sparkles, Settings,
  Phone, ChevronDown, ExternalLink,
  MessageSquare, Layout, RefreshCw, Image, Puzzle,
  Copy, X, Paperclip, Wand2, Check, Mic,
  ChevronUp, Layers, MousePointer, LayoutGrid, ClipboardList, Link2, Calendar,
  Undo2, Redo2, SlidersHorizontal,
  Code, Home, Map as MapIcon,
  BarChart3, History, Sun, Moon, TrendingUp, MonitorIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-frontend/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import StockImageModal from './StockImageModal';
import { TwoStepModelPicker } from './TwoStepModelPicker';
import { type LandingPage as BaseLandingPage, type ChatMessage, type ViewMode, type WidgetConfigState, type WidgetConfigDialogState, generateSectionId, insertAfterSection, DEFAULT_WIDGET_CONFIG, SECTION_TYPE_LABELS } from './landing-builder/types';
import type {
  LandingSection,
  LandingSettings,
  LandingPageDto,
  LandingSiteGroupDto,
  LandingLinkablePageDto,
  LandingAvailableWidgetsDto,
  LandingWidgetOption,
  LandingPageVersionDto,
  LandingUploadImageDto,
  LandingScrapeDto,
  LandingAiChatDto,
  LandingComponentLibraryDto,
  LandingComponentCategory,
  LandingComponentVariant,
  LandingTemplateDto,
  LandingFontPairDto,
  LandingGroupSnippetsDto,
  LandingAnalyticsDto,
  LandingServiceModelEntryDto,
} from '@easyflow/shared';
import { useUndoRedo } from './landing-builder/useUndoRedo';
import SectionPanel from './landing-builder/SectionPanel';
import PropertiesPanel from './landing-builder/PropertiesPanel';
import PreviewIframe from './landing-builder/PreviewIframe';
import WidgetConfigDialog from './landing-builder/WidgetConfigDialog';
import LandingAnalyticsDashboard from './landing-builder/LandingAnalyticsDashboard';

import { TrackingServiceCards } from './landing-builder/TrackingWizardModal';
import { getErrorMessage } from '../../lib/utils';


type LandingPage = BaseLandingPage & {
  analytics?: { total_views: number; views_7d: number; sparkline: number[] };
};

// props di una sezione widget (chatbot/voice/gemini). È jsonb DINAMICO: nessun
// contratto stretto lato wire, ma qui elenchiamo le chiavi note lette dalla UI
// con i tipi attesi dai campi di WidgetConfigState (union literali dove
// applicabile), così il narrowing avviene senza any. Tutto opzionale +
// index signature per tollerare le altre chiavi dinamiche del blob.
type WidgetSectionProps = {
  // chatbot_embed
  bubbleColor?: WidgetConfigState['bubbleColor'];
  bubblePosition?: WidgetConfigState['bubblePosition'];
  bubbleIcon?: WidgetConfigState['bubbleIcon'];
  bubbleSize?: WidgetConfigState['bubbleSize'];
  animation?: WidgetConfigState['animation'];
  sound?: WidgetConfigState['sound'];
  tooltip?: WidgetConfigState['tooltip'];
  vibration?: WidgetConfigState['vibration'];
  autoOpenDelay?: WidgetConfigState['autoOpenDelay'];
  // gemini_voice_embed
  color?: WidgetConfigState['gvColor'];
  position?: WidgetConfigState['gvPosition'] & WidgetConfigState['vaPosition'];
  ctaText?: WidgetConfigState['gvCtaText'];
  shape?: WidgetConfigState['gvShape'];
  liveTranscript?: WidgetConfigState['gvLiveTranscript'];
  showMute?: WidgetConfigState['gvShowMute'];
  overrideFirstMessage?: string;
  // voice_agent_embed
  variant?: WidgetConfigState['vaVariant'];
  firstMessage?: string;
  avatarImage?: string;
  avatarOrbColor1?: string;
  avatarOrbColor2?: string;
  showAvatarCollapsed?: boolean;
  language?: string;
  overridePrompt?: string;
  overrideVoiceId?: string;
  overrideSpeed?: string;
  overrideStability?: string;
  overrideSimilarityBoost?: string;
  textInput?: boolean;
  micMuting?: boolean;
  dismissible?: boolean;
  defaultExpanded?: boolean;
  alwaysExpanded?: boolean;
  transcript?: boolean;
  overrideTextOnly?: boolean;
  collectFeedback?: boolean;
  showAgentStatus?: boolean;
  dynamicVariables?: string; // JSON serializzato
  textContents?: string; // JSON serializzato
  agentId?: string;
  title?: string;
  [key: string]: unknown;
};

// Bersaglio di inserimento immagine scelto in StockImageModal (rispecchia
// l'`ImageTarget` del modal). `field` guida quale prop della sezione
// aggiornare; `index` per gli array (images/items).
type StockImageTarget = {
  label: string;
  sectionId: string;
  sectionType?: string;
  field: string;
  index?: number;
  currentUrl?: string;
};

function Sparkline({ data, width = 80, height = 24 }: { data: number[]; width?: number; height?: number }) {
  if (!data || data.length === 0 || data.every(v => v === 0)) {
    return <div style={{ width, height }} className="flex items-center justify-center text-xs text-gray-300">—</div>;
  }
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (v / max) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline points={points} fill="none" stroke="#0ea5e9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function LandingPageBuilder() {
  const { toast } = useToast();
  const [view, setView] = useState<ViewMode>('list');
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const tenantSlug = localStorage.getItem('edgvoip_tenant_slug') || 'demo';
  // Le pagine pubblicate vivono sullo stesso apex dove gira la SPA tenant
  // (es. `https://easyflowai.it/<slug>/site/<page>`). Niente hardcoding del
  // dominio: derivare da `window.location.origin` rende il builder a prova
  // di rebrand e coerente con i path serviti dall'edge nginx apex.
  const publicBaseUrl = `${window.location.origin}/${tenantSlug}/site`;

  const [editingPage, setEditingPage] = useState<LandingPage | null>(null);
  // Modello DINAMICO del site-builder: ogni sezione ha `type` (guida il render)
  // e `props` jsonb libero. Tipo largo condiviso (@easyflow/shared), MAI any.
  const [sections, setSections] = useState<LandingSection[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newPageStep, setNewPageStep] = useState<'templates' | 'details'>('templates');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [templates, setTemplates] = useState<LandingTemplateDto[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [newGroupId, setNewGroupId] = useState<string | null>(null);
  const [newProjectMode, setNewProjectMode] = useState<'standalone' | 'existing' | 'new'>('standalone');
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [movePageId, setMovePageId] = useState<string | null>(null);
  const [moveTargetGroup, setMoveTargetGroup] = useState<string>('');
  const [createTargetGroup, setCreateTargetGroup] = useState<string>('');
  const [siteGroups, setSiteGroups] = useState<{ id: string; name: string }[]>([]);

  const [showInitialChoice, setShowInitialChoice] = useState(false);
  const [canvasGroupId, setCanvasGroupId] = useState<string | null>(null);
  const [canvasGroupName, setCanvasGroupName] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [showProjectNameDialog, setShowProjectNameDialog] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);

  const [aiModel, setAiModel] = useState('');
  const [aiVisionModel, setAiVisionModel] = useState('');
  const [aiImageModel, setAiImageModel] = useState('');
  const [aiTemperature, setAiTemperature] = useState(0.7);
  const [fontPairs, setFontPairs] = useState<{ id: string; name: string; displayFont: string; bodyFont: string }[]>([]);
  const [childTemplates, setChildTemplates] = useState<LandingTemplateDto[]>([]);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');


  const [availableWidgets, setAvailableWidgets] = useState<LandingAvailableWidgetsDto>({ forms: [], chatbots: [], voiceAgents: [], geminiVoiceAgents: [], taskForms: [], appointmentForms: [] });
  const [showWidgetPanel, setShowWidgetPanel] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [lastUserPrompt, setLastUserPrompt] = useState<string>('');
  const [showScrapeInput, setShowScrapeInput] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapedContent, setScrapedContent] = useState<{ content: string; domain: string; images?: { url: string; alt?: string }[] } | null>(null);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [startedFromScratch, setStartedFromScratch] = useState(false);

  const [widgetConfigDialog, setWidgetConfigDialog] = useState<WidgetConfigDialogState | null>(null);
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfigState>({
    ...DEFAULT_WIDGET_CONFIG,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [enableWebSearch, setEnableWebSearch] = useState(true);
  const [enableImageGen, setEnableImageGen] = useState(true);
  const [showStockImageModal, setShowStockImageModal] = useState(false);
  const [stockImagePreselectedTarget, setStockImagePreselectedTarget] = useState<string | undefined>(undefined);

  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsPageId, setAnalyticsPageId] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<LandingAnalyticsDto | null>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [analyticsMode, setAnalyticsMode] = useState<'project' | 'page'>('page');
  const [analyticsGroupId, setAnalyticsGroupId] = useState<string | null>(null);
  const [analyticsCustomHeadCode, setAnalyticsCustomHeadCode] = useState('');

  const [versions, setVersions] = useState<LandingPageVersionDto[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showVersionsDropdown, setShowVersionsDropdown] = useState(false);

  const [showPageSettings, setShowPageSettings] = useState(false);
  const [showSitemapModal, setShowSitemapModal] = useState(false);

  const [groupSnippetDialog, setGroupSnippetDialog] = useState<{ groupId: string; headCode: string; bodyCode: string } | null>(null);
  const [savingGroupSnippet, setSavingGroupSnippet] = useState(false);

  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [showSectionPanel, setShowSectionPanel] = useState(false);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);

  const openWidgetEditDialog = (sectionId: string) => {
    const sec = sections.find(s => s.id === sectionId);
    if (!sec) return false;
    const wTypes = ['chatbot_embed', 'voice_agent_embed', 'gemini_voice_embed'];
    if (!sec.type || !wTypes.includes(sec.type)) return false;
    setShowPropertiesPanel(false);
    // props è jsonb DINAMICO della sezione widget: vista larga tipizzata
    // (WidgetSectionProps) per il narrowing dei campi noti senza any.
    const p = (sec.props ?? {}) as WidgetSectionProps;
    const idx = sections.findIndex(s => s.id === sectionId);
    if (sec.type === 'chatbot_embed') {
      setWidgetConfig(prev => ({
        ...prev,
        bubbleColor: p.bubbleColor || DEFAULT_WIDGET_CONFIG.bubbleColor,
        bubblePosition: p.bubblePosition || DEFAULT_WIDGET_CONFIG.bubblePosition,
        bubbleIcon: p.bubbleIcon || DEFAULT_WIDGET_CONFIG.bubbleIcon,
        bubbleSize: p.bubbleSize || DEFAULT_WIDGET_CONFIG.bubbleSize,
        animation: p.animation || DEFAULT_WIDGET_CONFIG.animation,
        sound: p.sound ?? DEFAULT_WIDGET_CONFIG.sound,
        tooltip: p.tooltip ?? DEFAULT_WIDGET_CONFIG.tooltip,
        vibration: p.vibration ?? DEFAULT_WIDGET_CONFIG.vibration,
        autoOpenDelay: p.autoOpenDelay || DEFAULT_WIDGET_CONFIG.autoOpenDelay,
      }));
    } else if (sec.type === 'gemini_voice_embed') {
      setWidgetConfig(prev => ({
        ...prev,
        gvColor: p.color || DEFAULT_WIDGET_CONFIG.gvColor,
        gvPosition: p.position || DEFAULT_WIDGET_CONFIG.gvPosition,
        gvCtaText: p.ctaText || DEFAULT_WIDGET_CONFIG.gvCtaText,
        gvShape: p.shape || DEFAULT_WIDGET_CONFIG.gvShape,
        gvLiveTranscript: p.liveTranscript ?? DEFAULT_WIDGET_CONFIG.gvLiveTranscript,
        gvShowMute: p.showMute ?? DEFAULT_WIDGET_CONFIG.gvShowMute,
        gvOverrideFirstMessage: p.overrideFirstMessage || '',
      }));
    } else {
      let dynVars: Array<{key: string; value: string}> = [];
      try { if (p.dynamicVariables) dynVars = Object.entries(JSON.parse(p.dynamicVariables)).map(([k, v]) => ({ key: k, value: String(v) })); } catch {}
      let textContents: Record<string, string> = {};
      try { if (p.textContents) textContents = JSON.parse(p.textContents); } catch {}
      setWidgetConfig(prev => ({
        ...prev,
        vaVariant: p.variant || '',
        vaPosition: p.position || DEFAULT_WIDGET_CONFIG.vaPosition,
        vaFirstMessage: p.firstMessage || '',
        vaAvatarImage: p.avatarImage || '',
        vaAvatarOrbColor1: p.avatarOrbColor1 || '',
        vaAvatarOrbColor2: p.avatarOrbColor2 || '',
        vaShowAvatarCollapsed: p.showAvatarCollapsed || false,
        vaLanguage: p.language || 'it',
        vaOverridePrompt: p.overridePrompt || '',
        vaOverrideVoiceId: p.overrideVoiceId || '',
        vaOverrideSpeed: p.overrideSpeed || '',
        vaOverrideStability: p.overrideStability || '',
        vaOverrideSimilarityBoost: p.overrideSimilarityBoost || '',
        vaTextInput: p.textInput || false,
        vaMicMuting: p.micMuting || false,
        vaDismissible: p.dismissible || false,
        vaDefaultExpanded: p.defaultExpanded || false,
        vaAlwaysExpanded: p.alwaysExpanded || false,
        vaTranscript: p.transcript || false,
        vaOverrideTextOnly: p.overrideTextOnly || false,
        vaCollectFeedback: p.collectFeedback || false,
        vaShowAgentStatus: p.showAgentStatus || false,
        vaDynamicVars: dynVars,
        vaTextContents: textContents,
      }));
    }
    setWidgetConfigDialog({
      open: true,
      type: sec.type as 'chatbot_embed' | 'voice_agent_embed' | 'gemini_voice_embed',
      widgetId: p.agentId || '',
      widgetName: p.title || sec.type,
      editMode: true,
      editIndex: idx,
    });
    return true;
  };
  const [showComponentPanel, setShowComponentPanel] = useState(false);
  const [componentLibrary, setComponentLibrary] = useState<LandingComponentCategory[]>([]);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<LandingComponentVariant | null>(null);

  const { undo, redo, canUndo, canRedo } = useUndoRedo(sections, setSections, editingPage?.id);

  const [editorTab, setEditorTab] = useState<'editor' | 'code'>('editor');
  const [customHeadCode, setCustomHeadCode] = useState('');
  const [customBodyCode, setCustomBodyCode] = useState('');
  const [customCss, setCustomCss] = useState('');
  const [linkablePages, setLinkablePages] = useState<{ id: string; slug: string; title: string; status: string; parent_id?: string | null; sectionAnchors?: { id: string; type: string; label: string }[] }[]>([]);
  const [newParentId, setNewParentId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedSectionId && sections.length > 0 && !sections.find((s) => s.id === selectedSectionId)) {
      setSelectedSectionId(null);
    }
  }, [sections, selectedSectionId]);

  // I 3 modelli del Site Builder (generazione pagine / vision / immagini) sono
  // configurazione GLOBALE per-tenant, NON per-pagina: vivono in
  // tenant_service_routing via i service_key `landing_fallback` / `landing_vision`
  // / `landing_image` e sono risolti lato backend da getPlatformModel(...). I
  // selettori leggono il valore corrente da GET /ai-tools/service-models e ogni
  // scelta lo persiste subito con PUT /ai-tools/service-models/:serviceKey. Qui
  // si idratano gli state che alimentano i picker + il badge del modello.
  useEffect(() => {
    if (view !== 'editor') return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await apiClient.get<LandingServiceModelEntryDto[]>('/ai-tools/service-models');
        if (cancelled || !res.success || !res.data) return;
        const byKey = (key: string): string =>
          res.data!.find((e) => e.service_key === key)?.current_model ?? '';
        setAiModel(byKey('landing_fallback'));
        setAiVisionModel(byKey('landing_vision'));
        setAiImageModel(byKey('landing_image'));
      } catch {
        /* fail-soft: i selettori restano vuoti, l'utente può sceglierli a mano */
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [view]);

  // Persiste la scelta di un modello del Site Builder nel routing GLOBALE del
  // tenant (tenant_service_routing). Payload canonico `{ provider, model }`: il
  // provider è quello selezionato nel picker, fonte di verità per il backend.
  const saveSiteBuilderModel = useCallback(
    async (serviceKey: string, modelId: string, provider: string): Promise<void> => {
      try {
        await apiClient.put(`/ai-tools/service-models/${serviceKey}`, { provider, model: modelId });
      } catch (err) {
        toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
      }
    },
    [toast],
  );

  const loadAvailableWidgets = useCallback(async () => {
    try {
      const res = await apiClient.get<LandingAvailableWidgetsDto>('/landing-pages/available-widgets');
      if (res.success && res.data) {
        setAvailableWidgets(res.data);
      }
    } catch (err) {
      console.error('Failed to load widgets:', err);
    }
  }, []);

  const loadLinkablePages = useCallback(async (groupId?: string | null) => {
    try {
      const gid = groupId !== undefined ? groupId : editingPage?.site_group_id;
      const url = gid ? `/landing-pages/linkable-pages?site_group_id=${gid}` : '/landing-pages/linkable-pages';
      const res = await apiClient.get<LandingLinkablePageDto[]>(url);
      if (res.success && res.data) {
        setLinkablePages(res.data as typeof linkablePages);
      }
    } catch (err) {
      console.error('Failed to load linkable pages:', err);
    }
  }, [editingPage?.site_group_id]);

  const loadComponentLibrary = useCallback(async () => {
    if (componentLibrary.length > 0) return;
    setLoadingComponents(true);
    try {
      const res = await apiClient.get<LandingComponentLibraryDto>('/landing-pages/component-library');
      if (res.success && res.data) {
        setComponentLibrary(res.data.categories || []);
      }
    } catch (err) {
      console.error('Failed to load component library:', err);
    } finally {
      setLoadingComponents(false);
    }
  }, [componentLibrary.length]);

  const uploadImage = async (file: File) => {
    setUploading(true);
    setUploadedImageUrl(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await apiClient.post<LandingUploadImageDto>('/landing-pages/upload-image', formData);
      if (res.success && res.data) {
        const url = res.data.url;
        setUploadedImageUrl(url);
        toast({ title: 'Caricata', description: 'Immagine caricata con successo' });
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const scrapeCompetitorUrl = async () => {
    if (!scrapeUrl.trim()) return;
    setScraping(true);
    try {
      const res = await apiClient.post<LandingScrapeDto>('/landing-pages/ai/scrape', { url: scrapeUrl.trim() });
      if (res.success && res.data) {
        const domain = new URL(scrapeUrl.trim()).hostname.replace('www.', '');
        const data = res.data;
        setScrapedContent({ content: data.content, domain, images: data.images || [] });
        toast({ title: 'Pagina analizzata', description: `Contenuto di ${domain} pronto per l'AI` });
        setShowScrapeInput(false);
        setScrapeUrl('');
      }
    } catch (err) {
      toast({ title: 'Errore scraping', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setScraping(false);
    }
  };

  const loadAnalytics = useCallback(async (pageId: string, days?: number) => {
    setLoadingAnalytics(true);
    try {
      const periodDays = days || 30;
      const from = new Date(Date.now() - periodDays * 86400000).toISOString().split('T')[0];
      const to = new Date().toISOString().split('T')[0];
      const res = await apiClient.get<LandingAnalyticsDto>(`/landing-pages/${pageId}/analytics?from=${from}&to=${to}`);
      if (res.success && res.data) setAnalyticsData(res.data);
      try {
        const pageRes = await apiClient.get<LandingPageDto>(`/landing-pages/${pageId}`);
        const pageData = pageRes?.data;
        const pageSettings = (pageData?.settings ?? null) as LandingSettings | null;
        setAnalyticsCustomHeadCode(pageSettings?.custom_head_code || '');
      } catch { /* ignore */ }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  const loadProjectAnalytics = useCallback(async (groupId: string, days?: number) => {
    setLoadingAnalytics(true);
    try {
      const periodDays = days || 30;
      const from = new Date(Date.now() - periodDays * 86400000).toISOString().split('T')[0];
      const to = new Date().toISOString().split('T')[0];
      const res = await apiClient.get<LandingAnalyticsDto>(`/landing-pages/project/${groupId}/analytics?from=${from}&to=${to}`);
      if (res.success && res.data) setAnalyticsData(res.data);
      try {
        // NB: /snippets risponde "nudo" (NON wrappato in { success, data }).
        // apiClient.get lo espone comunque su `.data`; fallback al root per
        // sicurezza sul contratto.
        const snippetRes = await apiClient.get<LandingGroupSnippetsDto>(`/landing-pages/site-group/${groupId}/snippets`);
        const snippetData = (snippetRes?.data ?? (snippetRes as unknown as LandingGroupSnippetsDto));
        setAnalyticsCustomHeadCode(snippetData?.custom_head_code || '');
      } catch { /* ignore */ }
    } catch (err) {
      console.error('Failed to load project analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  const loadVersions = useCallback(async (pageId: string) => {
    setLoadingVersions(true);
    try {
      const res = await apiClient.get<LandingPageVersionDto[]>(`/landing-pages/${pageId}/versions`);
      if (res.success && res.data) setVersions(res.data);
    } catch (err) {
      console.error('Failed to load versions:', err);
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  const restoreVersion = useCallback(async (pageId: string, versionId: string) => {
    try {
      const res = await apiClient.post<LandingPageDto>(`/landing-pages/${pageId}/versions/${versionId}/restore`, {});
      if (res.success && res.data) {
        const data = res.data;
        const restored: LandingSection[] = typeof data.sections === 'string'
          ? JSON.parse(data.sections)
          : ((data.sections as LandingSection[] | null | undefined) || []);
        setSections(restored);
        if (editingPage) {
          setEditingPage({ ...editingPage, settings: (data.settings as LandingPage['settings']) || editingPage.settings });
        }
        toast({ title: 'Ripristinato', description: 'Versione ripristinata con successo' });
        setShowVersionsDropdown(false);
        loadVersions(pageId);
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    }
  }, [editingPage, toast, loadVersions]);

  const updatePageSettings = useCallback((key: string, value: unknown) => {
    if (!editingPage) return;
    const updatedSettings = { ...(editingPage.settings || {}), [key]: value };
    setEditingPage({ ...editingPage, settings: updatedSettings });
  }, [editingPage]);

  const updateSeoMeta = useCallback((key: string, value: unknown) => {
    if (!editingPage) return;
    const updatedSeo = { ...(editingPage.seo_meta || {}), [key]: value };
    setEditingPage({ ...editingPage, seo_meta: updatedSeo });
  }, [editingPage]);

  const loadSystemPrompt = useCallback(async () => {
    try {
      const res = await apiClient.get<{ systemPrompt?: string }>('/landing-pages/ai/system-prompt');
      if (res.success && res.data) {
        setSystemPrompt(res.data.systemPrompt || '');
      }
    } catch (err) {
      console.error('Failed to load system prompt:', err);
    }
  }, []);

  const regeneratePreview = async () => {
    if (!lastUserPrompt || generating) return;
    setGenerating(true);
    try {
      const regenMessages = [
        { role: 'user', content: `Rigenera la landing page con le stesse specifiche: ${lastUserPrompt}` }
      ];
      const res = await apiClient.post<LandingAiChatDto>('/landing-pages/ai/chat', {
        messages: regenMessages,
        currentSections: sections.length > 0 ? sections : undefined,
        temperature: aiTemperature,
        enableWebSearch,
        enableImageGen,
        site_group_id: editingPage?.site_group_id || undefined,
        widgets: {
          forms: availableWidgets.forms.map((f) => ({ id: f.id, name: f.name })),
          chatbots: availableWidgets.chatbots.map((c) => ({ id: c.id, name: c.name })),
          voiceAgents: availableWidgets.voiceAgents.map((a) => ({ id: a.id, name: a.name })),
          taskForms: availableWidgets.taskForms.map((f) => ({ id: f.id, name: f.name, board_name: f.board_name })),
          appointmentForms: (availableWidgets.appointmentForms || []).map((f) => ({ id: f.id, name: f.name, slug: f.slug }))
        }
      });
      if (res.success && res.data) {
        const data = res.data;
        const newSections = data.sections || [];
        if (newSections.length > 0) setSections(newSections);
        let regenMsg = data.message || `Ho rigenerato la preview con ${newSections.length} sezioni.`;
        if (data.fallback && data.modelUsed && data.modelRequested) {
          regenMsg += `\n⚠️ Modello ${data.modelRequested} non disponibile, usato ${data.modelUsed} come fallback.`;
        }
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: regenMsg,
          sections: newSections
        }]);
      }
    } catch (err) {
      let userMessage = 'Errore di comunicazione con il servizio AI. Riprova.';
      const status = (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.response?.status;
      const msgText = (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || '';
      if (status === 504 || msgText.includes('troppo tempo') || msgText.includes('timeout')) {
        userMessage = 'La generazione sta impiegando troppo tempo. Riprova tra qualche secondo.';
      } else if (status === 401 || msgText.includes('non valida') || msgText.includes('non autorizzata')) {
        userMessage = 'Chiave OpenAI non valida o non autorizzata. Verificala nelle impostazioni AI.';
      } else if ((status && status >= 502 && status <= 504) || msgText.includes('non disponibile')) {
        userMessage = 'Servizio AI temporaneamente non disponibile. Riprova tra qualche secondo.';
      }
      toast({ title: 'Errore', description: userMessage, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<LandingPageDto[]>(`/landing-pages?status=${statusFilter}&search=${searchQuery}`);
      if (res.success && res.data) {
        setPages(res.data as unknown as LandingPage[]);
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
    try {
      const groupsRes = await apiClient.get<LandingSiteGroupDto[]>('/landing-pages/site-groups');
      if (groupsRes.success && groupsRes.data) {
        setSiteGroups(groupsRes.data);
      }
    } catch {}
  }, [statusFilter, searchQuery, toast]);

  useEffect(() => { loadPages(); }, [loadPages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const [tplRes, fpRes, ctRes] = await Promise.all([
        apiClient.get<LandingTemplateDto[]>('/landing-pages/templates'),
        apiClient.get<LandingFontPairDto[]>('/landing-pages/font-pairs'),
        apiClient.get<LandingTemplateDto[]>('/landing-pages/child-templates'),
      ]);
      if (tplRes.success && tplRes.data) setTemplates(tplRes.data);
      if (fpRes.success && fpRes.data) setFontPairs(fpRes.data);
      if (ctRes.success && ctRes.data) setChildTemplates(ctRes.data);
    } catch (err) { console.error('Failed to load templates:', err); }
    finally { setLoadingTemplates(false); }
  }, []);

  const createProjectGroup = async () => {
    if (!newProjectName.trim()) return;
    setCreatingProject(true);
    try {
      const res = await apiClient.post<LandingSiteGroupDto>('/landing-pages/site-groups', { name: newProjectName.trim() });
      if (res.success && res.data) {
        const group = res.data;
        setCanvasGroupId(group.id);
        setCanvasGroupName(group.name);
        setSiteGroups(prev => [...prev, { id: group.id, name: group.name }]);
        setShowProjectNameDialog(false);
        setNewProjectName('');
        setView('canvas');
        loadPages();
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setCreatingProject(false);
    }
  };

  const createPage = async () => {
    if (!newTitle.trim()) return;
    let slug = newSlug.trim() || newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (newParentId) {
      const parentPage = linkablePages.find(p => p.id === newParentId);
      if (parentPage && parentPage.slug && !slug.includes('/')) {
        slug = `${parentPage.slug}/${slug}`;
      }
    }

    let templateSections: LandingSection[] = [];
    let templateSettings: LandingSettings = {};
    if (selectedTemplate && selectedTemplate !== 'scratch') {
      try {
        const isChild = childTemplates.some((ct) => ct.id === selectedTemplate);
        const endpoint = isChild ? `/landing-pages/child-templates/${selectedTemplate}` : `/landing-pages/templates/${selectedTemplate}`;
        const tplRes = await apiClient.get<LandingTemplateDto>(endpoint);
        if (tplRes.success && tplRes.data) {
          templateSections = tplRes.data.sections || [];
          templateSettings = tplRes.data.settings || {};
        }
      } catch (err) { console.error('Failed to load template:', err); }
    }

    let siteGroupId: string | null = null;
    if (newGroupId) {
      siteGroupId = newGroupId;
    } else if (newProjectMode === 'new') {
      siteGroupId = crypto.randomUUID();
    } else if (newProjectMode === 'existing' && createTargetGroup) {
      siteGroupId = createTargetGroup;
    }

    try {
      const res = await apiClient.post<LandingPageDto>('/landing-pages', {
        title: newTitle, slug,
        site_group_id: siteGroupId,
        parent_id: newParentId,
        sections: templateSections,
        seo_meta: {},
        settings: { ...templateSettings, temperature: aiTemperature }
      });
      if (res.success && res.data) {
        setEditingPage(res.data as unknown as LandingPage);
        setSections(templateSections);

        const tplName = templates.find(t => t.id === selectedTemplate)?.name;
        const welcomeMsg = selectedTemplate && selectedTemplate !== 'scratch'
          ? `Ho caricato il template "${tplName}" con ${templateSections.length} sezioni professionali. La preview è già pronta!\n\nOra puoi personalizzarlo:\n- Cambia i testi (es. "cambia il titolo hero in...")\n- Modifica i colori (es. "usa il blu come colore primario")\n- Aggiungi o rimuovi sezioni\n- Inserisci le tue informazioni di contatto\n\nCosa vuoi modificare?`
          : 'Ciao! Sono il tuo consulente per siti web e landing page ad alta conversione.\n\nDescrivimi la tua attività e i tuoi obiettivi — analizzerò il tuo caso, ti proporrò una strategia personalizzata e, dopo la tua approvazione, genererò il sito completo.\n\nAd esempio: "Ho un ristorante di pesce a Milano, cucina mediterranea, voglio raccogliere prenotazioni online"';

        setCustomHeadCode('');
        setCustomBodyCode('');
        setCustomCss('');
        setEditorTab('editor');
        setChatMessages([{ role: 'assistant', content: welcomeMsg }]);
        setIsFirstMessage(true);
        setStartedFromScratch(selectedTemplate === 'scratch');
        setView('editor');
        setShowNewDialog(false);
        setNewTitle('');
        setNewSlug('');
        setSelectedTemplate(null);
        setNewPageStep('templates');
        setNewGroupId(null);
        setNewParentId(null);
        setNewProjectMode('standalone');
        setMoveTargetGroup('');
        loadAvailableWidgets();
        loadLinkablePages(res.data.site_group_id ?? null);
        loadPages();
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    }
  };

  const moveToGroup = async (pageId: string, groupId: string | null) => {
    try {
      await apiClient.put(`/landing-pages/${pageId}/move-to-group`, { site_group_id: groupId });
      toast({ title: 'Spostata', description: groupId ? 'Pagina spostata nel progetto' : 'Pagina resa standalone' });
      loadPages();
      setShowMoveDialog(false);
      setMovePageId(null);
      setMoveTargetGroup('');
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    }
  };

  const openEditor = async (page: LandingPage) => {
    try {
      const res = await apiClient.get<LandingPageDto>(`/landing-pages/${page.id}`);
      if (res.success && res.data) {
        const full = res.data;
        setEditingPage(full as unknown as LandingPage);
        setSelectedSectionId(null);
        setShowSectionPanel(false);
        const parsed: LandingSection[] = typeof full.sections === 'string'
          ? JSON.parse(full.sections)
          : ((full.sections as LandingSection[] | null | undefined) || []);
        setSections(parsed);
        const s = (full.settings ?? {}) as LandingSettings;
        if (typeof s.temperature === 'number') setAiTemperature(s.temperature);
        setCustomHeadCode(s.custom_head_code || '');
        setCustomBodyCode(s.custom_body_code || '');
        setCustomCss(s.custom_css || '');
        setEditorTab('editor');
        setChatMessages([{
          role: 'assistant',
          content: `Stai modificando "${full.title}". Dimmi cosa vuoi cambiare o aggiungere.`
        }]);
        setIsFirstMessage(true);
        setStartedFromScratch(false);
        setView('editor');
        loadAvailableWidgets();
        loadLinkablePages(full.site_group_id ?? null);
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    }
  };

  const handleStockImageSelect = useCallback((imageUrl: string, target: StockImageTarget | null | undefined) => {
    if (!target) {
      setChatInput(prev => {
        const prefix = prev.trim() ? prev.trim() + ' ' : '';
        return prefix + imageUrl;
      });
      toast({ title: 'URL immagine copiato', description: 'Incollato nel prompt. Invia il messaggio per usarla nella landing.' });
      return;
    }
    setSections(prev => {
      return prev.map(section => {
        if (section.id !== target.sectionId) return section;
        // props è jsonb dinamico: record mutabile largo per l'update in-place
        // dei campi immagine (backgroundImage/image/avatar/images[]/items[]).
        const props: Record<string, unknown> = { ...(section.props ?? {}) };
        const updated: LandingSection = { ...section, props };
        if (target.field === 'backgroundImage') {
          props.backgroundImage = imageUrl;
        } else if (target.field === 'image') {
          props.image = imageUrl;
        } else if (target.field === 'avatar') {
          props.avatar = imageUrl;
        } else if (target.field === 'images' && target.index !== undefined) {
          const images = [...(Array.isArray(props.images) ? props.images : [])] as Array<Record<string, unknown>>;
          if (images[target.index]) {
            images[target.index] = { ...images[target.index], url: imageUrl };
          }
          props.images = images;
        } else if (target.field === 'items_image' && target.index !== undefined) {
          const items = [...(Array.isArray(props.items) ? props.items : [])] as Array<Record<string, unknown>>;
          if (items[target.index]) {
            items[target.index] = { ...items[target.index], image: imageUrl };
          }
          props.items = items;
        } else if (target.field === 'items_url' && target.index !== undefined) {
          const items = [...(Array.isArray(props.items) ? props.items : [])] as Array<Record<string, unknown>>;
          if (items[target.index]) {
            items[target.index] = { ...items[target.index], url: imageUrl };
          }
          props.items = items;
        }
        return updated;
      });
    });
    toast({ title: 'Immagine inserita', description: `Immagine applicata a "${target.label}". Salva per confermare.` });
  }, [toast]);

  const sendChat = async (overrideForceGenerate?: boolean, overrideMessage?: string) => {
    const msgToSend = overrideMessage || chatInput.trim();
    if (!msgToSend || generating) return;
    let userMsg = msgToSend;
    if (selectedComponent) {
      userMsg = `[COMPONENTE SELEZIONATO: ${selectedComponent.name} - Tipo: ${selectedComponent.sectionType} - Props: ${JSON.stringify(selectedComponent.defaultProps)}]\n\n${userMsg}`;
      setSelectedComponent(null);
    }
    const currentAttachedImage = uploadedImageUrl;
    if (currentAttachedImage) {
      setUploadedImageUrl(null);
    }
    const currentScrapedContent = scrapedContent;
    if (currentScrapedContent) {
      setScrapedContent(null);
    }
    const shouldForceGenerate = overrideForceGenerate || false;
    setChatInput('');
    const updatedMessages = [...chatMessages, { role: 'user' as const, content: userMsg }];
    setChatMessages(updatedMessages);
    setLastUserPrompt(userMsg);
    setGenerating(true);
    setIsFirstMessage(false);

    try {
      const conversationHistory = updatedMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await apiClient.post<LandingAiChatDto>('/landing-pages/ai/chat', {
        messages: conversationHistory,
        currentSections: sections.length > 0 ? sections : undefined,
        selectedSectionId: selectedSectionId || undefined,
        temperature: aiTemperature,
        enableWebSearch,
        enableImageGen,
        attachedImageUrl: currentAttachedImage || undefined,
        scrapedContent: currentScrapedContent?.content || undefined,
        scrapedImages: currentScrapedContent?.images || undefined,
        forceGenerate: shouldForceGenerate || undefined,
        site_group_id: editingPage?.site_group_id || undefined,
        widgets: {
          forms: availableWidgets.forms.map((f) => ({ id: f.id, name: f.name })),
          chatbots: availableWidgets.chatbots.map((c) => ({ id: c.id, name: c.name })),
          voiceAgents: availableWidgets.voiceAgents.map((a) => ({ id: a.id, name: a.name })),
          taskForms: availableWidgets.taskForms.map((f) => ({ id: f.id, name: f.name, board_name: f.board_name })),
          appointmentForms: (availableWidgets.appointmentForms || []).map((f) => ({ id: f.id, name: f.name, slug: f.slug }))
        }
      });
      if (res.success && res.data) {
        const data = res.data;
        const newSections = data.sections || null;
        const imgSummary = data.imagesSummary || [];

        if (newSections && newSections.length > 0) {
          setSections(newSections);
        }

        let assistantMsg = data.message || '';
        if (imgSummary.length > 0) assistantMsg += ` (${imgSummary.join(', ')})`;
        if (data.imageGenResults) {
          const { generated: genCount, failed: failCount } = data.imageGenResults;
          if (genCount > 0 && failCount === 0) {
            toast({ title: 'Immagini AI', description: `${genCount} immagini generate con successo` });
          } else if (failCount > 0 && genCount > 0) {
            toast({ title: 'Immagini AI', description: `${genCount} generate, ${failCount} fallite (mantenute precedenti). Riprova per rigenerare.` });
            assistantMsg += `\n⚠️ ${failCount} immagini non generate (errore/timeout). Le precedenti sono state mantenute. Riscrivi il messaggio per riprovare.`;
          } else if (failCount > 0 && genCount === 0) {
            toast({ title: 'Immagini AI', description: `Generazione fallita per ${failCount} immagini. Le immagini precedenti sono state mantenute.`, variant: 'destructive' });
            assistantMsg += `\n⚠️ Generazione immagini fallita. Le precedenti sono state mantenute. Riscrivi il messaggio per riprovare.`;
          }
        }
        if (data.fallback && data.modelUsed && data.modelRequested) {
          assistantMsg += `\n⚠️ Modello ${data.modelRequested} non disponibile, usato ${data.modelUsed} come fallback.`;
        }

        setChatMessages(prev => [...prev, {
          role: 'assistant' as const,
          content: assistantMsg,
          sections: newSections || undefined,
          phase: (data.phase === 'analysis' || data.phase === 'proposal' || data.phase === 'generation')
            ? data.phase
            : undefined
        }]);
      }
    } catch (err) {
      let userMessage = 'Errore di comunicazione con il servizio AI. Riprova.';
      const status = (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.response?.status;
      const msg = (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string }; status?: number }; message?: string; code?: string; status?: number })?.message || '';
      if (status === 504 || msg.includes('troppo tempo') || msg.includes('timeout')) {
        userMessage = 'La generazione sta impiegando troppo tempo. Riprova con un messaggio più breve.';
      } else if (status === 401 || msg.includes('non valida') || msg.includes('non autorizzata')) {
        userMessage = 'Chiave OpenAI non valida o non autorizzata. Verificala nelle impostazioni AI.';
      } else if ((status && status >= 502 && status <= 504) || msg.includes('non disponibile')) {
        userMessage = 'Servizio AI temporaneamente non disponibile. Riprova tra qualche secondo.';
      }
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: userMessage
      }]);
    } finally {
      setGenerating(false);
    }
  };

  const savePage = async () => {
    if (!editingPage) return;
    setSaving(true);
    try {
      const existingSettings = editingPage.settings || {};
      await apiClient.put(`/landing-pages/${editingPage.id}`, {
        sections,
        seo_meta: editingPage.seo_meta || {},
        settings: {
          ...existingSettings,
          temperature: aiTemperature,
          custom_head_code: customHeadCode || undefined,
          custom_body_code: customBodyCode || undefined,
          custom_css: customCss || undefined,
        }
      });
      toast({ title: 'Salvato', description: 'Progetto salvato con successo' });
    } catch (err) {
      if (getErrorMessage(err)?.includes('not found') || getErrorMessage(err)?.includes('404')) {
        try {
          const retrySettings = editingPage.settings || {};
          const res = await apiClient.post<LandingPageDto>('/landing-pages', {
            title: editingPage.title,
            slug: editingPage.slug,
            description: editingPage.description || '',
            sections,
            settings: { ...retrySettings, temperature: aiTemperature }
          });
          if (res.data?.id) {
            setEditingPage({ ...editingPage, id: res.data.id });
          }
          toast({ title: 'Salvato', description: 'Progetto ricreato e salvato' });
          loadPages();
          return;
        } catch (retryErr) {
          toast({ title: 'Errore', description: getErrorMessage(retryErr), variant: 'destructive' });
          return;
        }
      }
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await apiClient.put(`/landing-pages/${id}/status`, { status });
      const page = pages.find(p => p.id === id) || editingPage;
      if (status === 'published' && page) {
        toast({ title: 'Pubblicata!', description: `Disponibile su: ${publicBaseUrl}/${page.slug}` });
      } else {
        toast({ title: 'Aggiornato', description: `Stato aggiornato a ${status === 'archived' ? 'Archiviata' : 'Bozza'}` });
      }
      loadPages();
      if (editingPage && editingPage.id === id) {
        setEditingPage({ ...editingPage, status: status as LandingPage['status'] });
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    }
  };

  const deletePage = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo progetto?')) return;
    try {
      await apiClient.delete(`/landing-pages/${id}`);
      toast({ title: 'Eliminato', description: 'Progetto eliminato' });
      loadPages();
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'published': return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Pubblicata</Badge>;
      case 'archived': return <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Archiviata</Badge>;
      default: return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">Bozza</Badge>;
    }
  };

  // Modale Impostazioni Site Builder UNICO: stesso markup in tutte le branch di
  // render (editor / lista / canvas) per evitare duplicazione e divergenze. Le
  // 3 select condividono `hideDeprecated` (chat/vision) e la SoT curata
  // (immagini). La select immagine è resa solo a generazione immagini ON.
  const renderSettingsDialog = (): JSX.Element => (
    <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-sky-500" />
            Impostazioni Site Builder
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <TwoStepModelPicker
            label="Modello AI per generazione pagine"
            helpText="Modello usato per generare e modificare le landing page"
            capability="chat"
            serviceKey="landing_fallback"
            value={aiModel}
            onChange={(modelId, provider) => { setAiModel(modelId); void saveSiteBuilderModel('landing_fallback', modelId, provider); }}
            hideDeprecated
          />

          <TwoStepModelPicker
            label="Modello vision (analisi immagini)"
            helpText="Usato quando alleghi un'immagine alla chat per ispirare la generazione"
            capability="vision"
            serviceKey="landing_vision"
            value={aiVisionModel}
            onChange={(modelId, provider) => { setAiVisionModel(modelId); void saveSiteBuilderModel('landing_vision', modelId, provider); }}
            hideDeprecated
          />

          {enableImageGen && (
            <TwoStepModelPicker
              label="Modello generazione immagini"
              helpText="Usato per generare le immagini delle sezioni quando la generazione immagini è attiva"
              capability="image"
              serviceKey="landing_image"
              value={aiImageModel}
              onChange={(modelId, provider) => { setAiImageModel(modelId); void saveSiteBuilderModel('landing_image', modelId, provider); }}
              hideDeprecated
            />
          )}

          <div className="space-y-2">
            <Label className="text-sm font-semibold">Prompt di sistema (sola lettura)</Label>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 max-h-48 overflow-y-auto">
              <pre className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
                {systemPrompt || 'Caricamento...'}
              </pre>
            </div>
            <p className="text-xs text-gray-500">Questo prompt guida l'AI nella generazione delle landing page</p>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">Funzionalità abilitate</Label>
            <div className="space-y-3">
              <label className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer">
                <div className="flex items-center gap-3">
                  <Image className="h-4 w-4 text-sky-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Ricerca web</p>
                    <p className="text-xs text-gray-500">Analizza automaticamente URL incollati in chat</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={enableWebSearch}
                  onChange={(e) => setEnableWebSearch(e.target.checked)}
                  className="h-4 w-4 text-sky-500 rounded border-gray-300 focus:ring-sky-500"
                />
              </label>
              <label className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer">
                <div className="flex items-center gap-3">
                  <Wand2 className="h-4 w-4 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Generazione immagini AI</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={enableImageGen}
                  onChange={(e) => setEnableImageGen(e.target.checked)}
                  className="h-4 w-4 text-purple-500 rounded border-gray-300 focus:ring-purple-500"
                />
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => setShowSettingsDialog(false)} className="bg-sky-500 hover:bg-sky-600 text-white">
            Fatto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (view === 'editor' && editingPage) {
    return (
      <>
      <div className="flex flex-col h-[calc(100vh-140px)] border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        {editingPage.site_group_id && pages.filter(p => p.site_group_id === editingPage.site_group_id).length > 0 && (
          <div className="flex items-center gap-1 px-4 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-x-auto">
            {pages.filter(p => p.site_group_id === editingPage.site_group_id).map((p) => (
              <button
                key={p.id}
                onClick={() => { if (p.id !== editingPage.id) openEditor(p); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                  p.id === editingPage.id
                    ? 'bg-white dark:bg-gray-800 text-sky-600 dark:text-sky-400 shadow-sm border border-gray-200 dark:border-gray-700'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-800/50'
                }`}
              >
                {p.status === 'published' && <Globe className="h-3 w-3 text-green-500" />}
                {p.title}
              </button>
            ))}
            <button
              onClick={() => { setNewGroupId(editingPage.site_group_id); setShowNewDialog(true); setNewPageStep('templates'); setSelectedTemplate(null); loadTemplates(); }}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-gray-400 hover:text-sky-500 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors whitespace-nowrap"
              title="Nuova pagina nel progetto"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => {
              loadPages();
              if (editingPage?.site_group_id && canvasGroupId === editingPage.site_group_id) {
                setView('canvas');
              } else {
                setView('list');
              }
            }}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
            </Button>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{editingPage.title}</h3>
              <p className="text-xs text-gray-500">
                {editingPage.status === 'published' ? (
                  <a href={`${publicBaseUrl}/${editingPage.slug}`} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:underline inline-flex items-center gap-1">
                    /{tenantSlug}/site/{editingPage.slug} <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span>/{tenantSlug}/site/{editingPage.slug}</span>
                )}
              </p>
            </div>
            <div className="flex items-center ml-3 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setEditorTab('editor')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                  editorTab === 'editor'
                    ? 'bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <Layout className="h-3.5 w-3.5" /> Editor
              </button>
              <button
                onClick={() => setEditorTab('code')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-200 dark:border-gray-700 ${
                  editorTab === 'code'
                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <Code className="h-3.5 w-3.5" /> Codice
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs px-2 py-1 text-gray-500 cursor-pointer hover:border-sky-400 hover:text-sky-600 transition-colors" onClick={() => setShowSettingsDialog(true)} title="Cambia modello AI">{aiModel}</Badge>
            <Button variant="outline" size="sm" onClick={undo} disabled={!canUndo} title="Annulla (Ctrl+Z)">
              <Undo2 className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={redo} disabled={!canRedo} title="Ripeti (Ctrl+Y)">
              <Redo2 className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowSectionPanel(!showSectionPanel)} title="Pannello Sezioni" className={showSectionPanel ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300' : ''}>
              <Layers className={`h-4 w-4 ${showSectionPanel ? 'text-indigo-600' : ''}`} />
              {sections.length > 0 && <span className="ml-1 text-xs">{sections.length}</span>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => { if (selectedSectionId) { setShowPropertiesPanel(!showPropertiesPanel); } else { setShowPropertiesPanel(false); } }} disabled={!selectedSectionId} title="Proprietà Sezione" className={showPropertiesPanel && selectedSectionId ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-300' : ''}>
              <SlidersHorizontal className={`h-4 w-4 ${showPropertiesPanel && selectedSectionId ? 'text-violet-600' : ''}`} />
            </Button>
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => { setShowVersionsDropdown(!showVersionsDropdown); if (!showVersionsDropdown) loadVersions(editingPage.id); }} title="Cronologia Versioni">
                <History className="h-4 w-4 mr-1" /> Versioni
              </Button>
              {showVersionsDropdown && (
                <div className="absolute right-0 top-full mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                  <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-semibold text-gray-500">Cronologia versioni</p>
                  </div>
                  {loadingVersions ? (
                    <div className="p-4 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>
                  ) : versions.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-400">Nessuna versione salvata</div>
                  ) : versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-50 dark:border-gray-700/50 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">v{v.version_number}</p>
                        <p className="text-xs text-gray-400">{new Date(v.created_at).toLocaleString('it-IT')}</p>
                      </div>
                      <Button size="sm" variant="ghost" className="text-xs text-sky-500 hover:text-sky-600" onClick={() => restoreVersion(editingPage.id, v.id)}>
                        Ripristina
                      </Button>
                    </div>
                  ))}
                  <button onClick={() => setShowVersionsDropdown(false)} className="w-full p-2 text-xs text-center text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50">Chiudi</button>
                </div>
              )}
            </div>
            {editingPage.site_group_id && (
              <Button variant="outline" size="sm" onClick={() => setShowSitemapModal(true)} title="Sitemap Progetto" className={showSitemapModal ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300' : ''}>
                <MapIcon className={`h-4 w-4 ${showSitemapModal ? 'text-emerald-600' : ''}`} />
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowPageSettings(!showPageSettings)} title="Impostazioni Pagina" className={showPageSettings ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300' : ''}>
              <Settings className={`h-4 w-4 ${showPageSettings ? 'text-purple-600' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" onClick={regeneratePreview} disabled={generating || !lastUserPrompt} title="Ricarica Preview">
              <RefreshCw className={`h-4 w-4 mr-1 ${generating ? 'animate-spin' : ''}`} /> Ricarica Preview
            </Button>
            <Button variant="outline" size="sm" onClick={savePage} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
              Salva
            </Button>
            <Button size="sm" onClick={() => updateStatus(editingPage.id, 'published')} className="bg-green-600 hover:bg-green-700 text-white">
              <Globe className="h-4 w-4 mr-1" /> Pubblica
            </Button>
          </div>
        </div>

        {editorTab === 'code' ? (
          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-6">
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="flex items-center gap-2 mb-2">
                <Code className="h-5 w-5 text-amber-500" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Codice Personalizzato</h3>
              </div>
              <p className="text-sm text-gray-500">Configura i servizi di tracking, inietta codice personalizzato e script esterni. Tutto viene inserito nel documento HTML finale sia in preview che nella pagina pubblicata.</p>

              <TrackingServiceCards
                customHeadCode={customHeadCode}
                onCustomHeadCodeChange={(code) => setCustomHeadCode(code)}
                siteGroupId={editingPage?.site_group_id || null}
                pageId={editingPage?.id || null}
                mode={editingPage?.site_group_id ? 'project' : 'page'}
              />

              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[10px] font-bold">&lt;/&gt;</span>
                  Head personalizzato <span className="text-xs font-normal text-gray-400">(iniettato prima di &lt;/head&gt;)</span>
                </Label>
                <p className="text-xs text-gray-400">Per tag &lt;script&gt;, &lt;meta&gt;, &lt;link&gt; — GA4, GTM, Meta Pixel, schema.org JSON-LD, ecc.</p>
                <textarea
                  value={customHeadCode}
                  onChange={(e) => setCustomHeadCode(e.target.value)}
                  placeholder={'<!-- Google Analytics 4 -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag(\'js\', new Date());\n  gtag(\'config\', \'G-XXXXXXX\');\n</script>'}
                  className="w-full min-h-[160px] text-xs font-mono rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-gray-800 dark:text-gray-200 resize-y focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 focus:border-blue-500"
                  spellCheck={false}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-[10px] font-bold">JS</span>
                  Body (prima di &lt;/body&gt;) <span className="text-xs font-normal text-gray-400">(iniettato prima di &lt;/body&gt;)</span>
                </Label>
                <p className="text-xs text-gray-400">Per script che vanno in fondo alla pagina — chat widget, script di terze parti, ecc.</p>
                <textarea
                  value={customBodyCode}
                  onChange={(e) => setCustomBodyCode(e.target.value)}
                  placeholder={'<!-- Hotjar Tracking -->\n<script>\n  (function(h,o,t,j,a,r){\n    // Hotjar tracking code\n  })(window,document,\'https://static.hotjar.com/c/hotjar-\',\'.js?sv=\');\n</script>'}
                  className="w-full min-h-[160px] text-xs font-mono rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-gray-800 dark:text-gray-200 resize-y focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500 focus:border-purple-500"
                  spellCheck={false}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold">CSS</span>
                  CSS personalizzato <span className="text-xs font-normal text-gray-400">(iniettato dopo il CSS principale)</span>
                </Label>
                <p className="text-xs text-gray-400">CSS aggiuntivo che sovrascrive lo stile base della pagina.</p>
                <textarea
                  value={customCss}
                  onChange={(e) => setCustomCss(e.target.value)}
                  placeholder={'/* Esempio: cambia colore dei titoli */\n.lp-hero-title {\n  color: #ff6600;\n}\n\n/* Nascondi il footer */\n.lp-footer {\n  display: none;\n}'}
                  className="w-full min-h-[160px] text-xs font-mono rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-gray-800 dark:text-gray-200 resize-y focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500 focus:border-emerald-500"
                  spellCheck={false}
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button onClick={savePage} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileText className="h-4 w-4 mr-1" />}
                  Salva Codice
                </Button>
                <p className="text-xs text-gray-400">Il codice verrà iniettato sia in preview che nella pagina pubblicata.</p>
              </div>

              {editingPage.site_group_id && linkablePages.filter(lp => lp.id !== editingPage.id).length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <Link2 className="h-4 w-4 text-sky-500" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Pagine del Progetto</h4>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">Click per copiare il link. Usa /{'{slug}'} nei campi CTA/button delle sezioni.</p>
                  <div className="space-y-1.5">
                    {linkablePages.filter(lp => lp.id !== editingPage.id).map(lp => (
                      <button
                        key={lp.id}
                        onClick={() => { navigator.clipboard.writeText(`/${lp.slug}`); toast({ title: 'Copiato', description: `Link /${lp.slug} copiato` }); }}
                        className="flex items-center gap-2 p-2 w-full text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-sky-300 hover:bg-sky-50/50 dark:hover:bg-sky-900/10 transition-colors"
                      >
                        {lp.status === 'published' ? <Globe className="h-3 w-3 text-green-500 shrink-0" /> : <FileText className="h-3 w-3 text-gray-400 shrink-0" />}
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 flex-1">{lp.title}</span>
                        <code className="text-[10px] font-mono text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-900/20 px-2 py-0.5 rounded">/{lp.slug}</code>
                        <Copy className="h-3 w-3 text-gray-300" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
        <div className="flex flex-1 overflow-hidden">
          <div className="w-[400px] min-w-[400px] flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-sky-500 text-white rounded-br-md'
                      : msg.phase === 'proposal'
                        ? 'bg-amber-50 dark:bg-amber-900/20 text-gray-900 dark:text-white border-2 border-amber-300 dark:border-amber-600 rounded-bl-md'
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-bl-md'
                  }`}>
                    {msg.phase === 'proposal' && (
                      <div className="flex items-center gap-1.5 mb-2 text-amber-600 dark:text-amber-400 font-semibold text-xs uppercase tracking-wide">
                        <ClipboardList className="h-3.5 w-3.5" />
                        Piano Proposto
                      </div>
                    )}
                    <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{
                      __html: msg.content
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n/g, '<br />')
                    }} />
                    {msg.phase === 'proposal' && idx === chatMessages.length - 1 && (
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => sendChat(false, 'Procedi, genera il sito con questa struttura')}
                          disabled={generating}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Check className="h-3.5 w-3.5" />
                          Approva e Genera
                        </button>
                        <button
                          onClick={() => {
                            const input = document.querySelector('[data-chat-input]') as HTMLElement | null;
                            if (input) input.focus();
                          }}
                          disabled={generating}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Edit className="h-3.5 w-3.5" />
                          Modifica Piano
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {generating && (
                <div className="flex justify-start">
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-2.5 rounded-2xl rounded-bl-md text-sm flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-sky-500" />
                    <span className="text-gray-500">Generazione in corso...</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 space-y-2">
              {selectedSectionId && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800 text-xs">
                  <MousePointer className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />
                  <span className="text-indigo-700 dark:text-indigo-300 font-medium truncate flex-1">
                    Modifica: {(() => {
                      const sec = sections.find((s) => s.id === selectedSectionId);
                      if (!sec) return selectedSectionId;
                      const typeLabels: Record<string, string> = { navbar: 'Navbar', hero: 'Hero', text: 'Testo', features: 'Features', testimonials: 'Testimonial', cta: 'CTA', pricing: 'Prezzi', faq: 'FAQ', contact: 'Contatti', stats: 'Statistiche', gallery: 'Galleria', form_embed: 'Form CRM', chatbot_embed: 'Chatbot', voice_agent_embed: 'Voice Agent', gemini_voice_embed: 'Gemini Voice', sms_button: 'SMS', whatsapp_button: 'WhatsApp', logos: 'Loghi', google_map: 'Google Maps', appointment_form_embed: 'Form Prenotazione' };
                      const secTitle = typeof sec.props?.title === 'string' ? sec.props.title : '';
                      return secTitle || (sec.type ? typeLabels[sec.type] : undefined) || sec.type;
                    })()}
                  </span>
                  <button onClick={() => setSelectedSectionId(null)} className="text-indigo-400 hover:text-indigo-600" title="Deseleziona">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {uploadedImageUrl && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800 text-xs">
                  <Image className="h-3.5 w-3.5 text-sky-500 flex-shrink-0" />
                  <span className="text-gray-600 dark:text-gray-300 truncate flex-1">{uploadedImageUrl}</span>
                  <button onClick={() => { navigator.clipboard.writeText(uploadedImageUrl); toast({ title: 'Copiato', description: 'URL copiato negli appunti' }); }} className="text-sky-500 hover:text-sky-600" title="Copia URL">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setUploadedImageUrl(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {showWidgetPanel && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-48 overflow-y-auto">
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">Widget Disponibili</span>
                    <button onClick={() => setShowWidgetPanel(false)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  {availableWidgets.chatbots.length === 0 && availableWidgets.voiceAgents.length === 0 && availableWidgets.taskForms.length === 0 ? (
                    <div className="p-3 text-center text-xs text-gray-500">Nessun widget disponibile</div>
                  ) : (
                    <>
                      {availableWidgets.chatbots.length > 0 && (
                        <div className="p-1.5">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase px-2 py-0.5">Chatbot</p>
                          {(() => {
                            const chatbotAlreadyAdded = sections.some((s) => s.type === 'chatbot_embed');
                            return availableWidgets.chatbots.map((chatbot) => (
                              <button
                                key={chatbot.id}
                                disabled={chatbotAlreadyAdded}
                                onClick={() => {
                                  if (chatbotAlreadyAdded) return;
                                  setWidgetConfig(prev => ({
                                    ...prev,
                                    bubbleColor: '#6366f1', bubblePosition: 'bottom-right' as const,
                                    bubbleIcon: 'chat' as const, bubbleSize: '56' as const,
                                    animation: 'bounce' as const, sound: true, tooltip: 'Chatta con noi!',
                                    vibration: true, autoOpenDelay: '0' as const,
                                  }));
                                  setWidgetConfigDialog({ open: true, type: 'chatbot_embed', widgetId: chatbot.id ?? '', widgetName: chatbot.name ?? '' });
                                  setShowWidgetPanel(false);
                                }}
                                className={`w-full px-2 py-1.5 text-left text-xs rounded flex items-center gap-2 ${chatbotAlreadyAdded ? 'opacity-50 cursor-not-allowed bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                              >
                                {chatbotAlreadyAdded ? <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" /> : <MessageSquare className="h-3.5 w-3.5 text-sky-500 flex-shrink-0" />}
                                <span className="text-gray-900 dark:text-white truncate">{chatbot.name}</span>
                                {chatbotAlreadyAdded && <span className="text-[9px] text-green-600 ml-auto">Inserito</span>}
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                      {availableWidgets.voiceAgents.length > 0 && (
                        <div className="p-1.5 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase px-2 py-0.5">Agenti Vocali</p>
                          {(() => {
                            const voiceAlreadyAdded = sections.some((s) => s.type === 'voice_agent_embed');
                            return availableWidgets.voiceAgents.map((agent) => (
                              <button
                                key={agent.id}
                                disabled={voiceAlreadyAdded}
                                onClick={() => {
                                  if (voiceAlreadyAdded) return;
                                  setWidgetConfig(prev => ({
                                    ...prev,
                                    vaVariant: '' as const, vaPosition: 'bottom-right' as const,
                                    vaFirstMessage: '', vaAvatarImage: '',
                                    vaAvatarOrbColor1: '', vaAvatarOrbColor2: '', vaShowAvatarCollapsed: false,
                                    vaLanguage: 'it', vaOverridePrompt: '', vaOverrideVoiceId: '',
                                    vaOverrideSpeed: '', vaOverrideStability: '', vaOverrideSimilarityBoost: '',
                                    vaTextInput: false, vaMicMuting: false, vaDismissible: false,
                                    vaDefaultExpanded: false, vaAlwaysExpanded: false, vaTranscript: false,
                                    vaOverrideTextOnly: false, vaCollectFeedback: false, vaShowAgentStatus: false,
                                    vaDynamicVars: [],
                                    vaTextContents: {},
                                  }));
                                  setWidgetConfigDialog({ open: true, type: 'voice_agent_embed', widgetId: agent.id ?? '', widgetName: agent.name ?? '' });
                                  setShowWidgetPanel(false);
                                }}
                                className={`w-full px-2 py-1.5 text-left text-xs rounded flex items-center gap-2 ${voiceAlreadyAdded ? 'opacity-50 cursor-not-allowed bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                              >
                                {voiceAlreadyAdded ? <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" /> : <Phone className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />}
                                <span className="text-gray-900 dark:text-white truncate">{agent.name}</span>
                                {voiceAlreadyAdded && <span className="text-[9px] text-green-600 ml-auto">Inserito</span>}
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                      {(availableWidgets.geminiVoiceAgents || []).length > 0 && (
                        <div className="p-1.5 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase px-2 py-0.5">Agenti Gemini Voice</p>
                          {(() => {
                            const geminiAlreadyAdded = sections.some((s) => s.type === 'gemini_voice_embed');
                            return availableWidgets.geminiVoiceAgents.map((agent) => (
                              <button
                                key={agent.id}
                                disabled={geminiAlreadyAdded}
                                onClick={() => {
                                  if (geminiAlreadyAdded) return;
                                  setWidgetConfig(prev => ({
                                    ...prev,
                                    gvColor: '#4285f4',
                                    gvPosition: 'bottom-right' as const,
                                    gvCtaText: 'Parla con noi',
                                    gvShape: 'circle' as const,
                                    gvLiveTranscript: false,
                                    gvShowMute: true,
                                    gvOverrideFirstMessage: '',
                                    gvOverridePrompt: '',
                                  }));
                                  setWidgetConfigDialog({ open: true, type: 'gemini_voice_embed', widgetId: agent.id ?? '', widgetName: agent.name ?? '' });
                                  setShowWidgetPanel(false);
                                }}
                                className={`w-full px-2 py-1.5 text-left text-xs rounded flex items-center gap-2 ${geminiAlreadyAdded ? 'opacity-50 cursor-not-allowed bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                              >
                                {geminiAlreadyAdded ? <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" /> : <Mic className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                                <span className="text-gray-900 dark:text-white truncate">{agent.name}</span>
                                <span className="text-[9px] text-blue-500 ml-auto">Gemini</span>
                                {geminiAlreadyAdded && <span className="text-[9px] text-green-600 ml-auto">Inserito</span>}
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                      {(availableWidgets.appointmentForms || []).length > 0 && (
                        <div className="p-1.5 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase px-2 py-0.5">Prenotazione Appuntamenti</p>
                          {availableWidgets.appointmentForms.map((form) => {
                            const alreadyAdded = sections.some((s) => s.type === 'appointment_form_embed' && s.props?.formSlug === form.slug);
                            return (
                              <button
                                key={form.id}
                                disabled={alreadyAdded}
                                onClick={() => {
                                  if (alreadyAdded) return;
                                  const newSection = {
                                    id: `appointment_form_${Date.now()}`,
                                    type: 'appointment_form_embed',
                                    props: { formSlug: form.slug, title: form.name, subtitle: form.auto_confirm ? 'Conferma immediata' : 'Richiede approvazione' }
                                  };
                                  setSections((prev) => [...prev, newSection]);
                                  setShowWidgetPanel(false);
                                }}
                                className={`w-full px-2 py-1.5 text-left text-xs rounded flex items-center gap-2 ${alreadyAdded ? 'opacity-50 cursor-not-allowed bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                              >
                                {alreadyAdded ? <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" /> : <Calendar className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                                <span className="text-gray-900 dark:text-white truncate">{form.name}</span>
                                <span className="text-[9px] text-gray-400 ml-auto truncate max-w-[80px]">/{form.slug}</span>
                                {alreadyAdded && <span className="text-[9px] text-green-600 ml-auto">Inserito</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {availableWidgets.taskForms.length > 0 && (
                        <div className="p-1.5 border-t border-gray-200 dark:border-gray-700">
                          <p className="text-[10px] font-semibold text-gray-400 uppercase px-2 py-0.5">Ticketing / TODO</p>
                          {availableWidgets.taskForms.map((form) => {
                            const alreadyAdded = sections.some((s) => s.type === 'task_form_embed' && s.props?.formId === form.id);
                            return (
                              <button
                                key={form.id}
                                disabled={alreadyAdded}
                                onClick={() => {
                                  if (alreadyAdded) return;
                                  const newSection = {
                                    id: `task_form_${Date.now()}`,
                                    type: 'task_form_embed',
                                    props: { formId: form.id, title: form.name, subtitle: form.board_name ? `Board: ${form.board_name}` : '' }
                                  };
                                  setSections((prev) => [...prev, newSection]);
                                  setShowWidgetPanel(false);
                                }}
                                className={`w-full px-2 py-1.5 text-left text-xs rounded flex items-center gap-2 ${alreadyAdded ? 'opacity-50 cursor-not-allowed bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                              >
                                {alreadyAdded ? <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" /> : <ClipboardList className="h-3.5 w-3.5 text-violet-500 flex-shrink-0" />}
                                <span className="text-gray-900 dark:text-white truncate">{form.name}</span>
                                {form.board_name && <span className="text-[9px] text-gray-400 ml-auto truncate max-w-[80px]">{form.board_name}</span>}
                                {alreadyAdded && <span className="text-[9px] text-green-600 ml-auto">Inserito</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {selectedComponent && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 text-xs">
                  <LayoutGrid className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  <span className="text-emerald-700 dark:text-emerald-300 font-medium truncate flex-1">
                    {selectedComponent.name}
                  </span>
                  <span className="text-[9px] text-emerald-500">Personalizza con AI</span>
                  <button onClick={() => setSelectedComponent(null)} className="text-emerald-400 hover:text-emerald-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {showComponentPanel && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto">
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                      <LayoutGrid className="h-3.5 w-3.5" /> Catalogo Componenti
                    </span>
                    <button onClick={() => setShowComponentPanel(false)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                  </div>
                  {loadingComponents ? (
                    <div className="p-3 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento...
                    </div>
                  ) : componentLibrary.length === 0 ? (
                    <div className="p-3 text-center text-xs text-gray-500">Nessun componente disponibile</div>
                  ) : (
                    <div className="p-1">
                      {componentLibrary.map((cat) => (
                        <div key={cat.id}>
                          <button
                            onClick={() => setExpandedCategory(expandedCategory === cat.id ? null : cat.id)}
                            className="w-full px-2 py-1.5 text-left text-xs rounded flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <span className="font-semibold text-gray-700 dark:text-gray-200">{cat.name}</span>
                            <span className="flex items-center gap-1">
                              <span className="text-[9px] text-gray-400">{cat.components.length}</span>
                              {expandedCategory === cat.id ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
                            </span>
                          </button>
                          {expandedCategory === cat.id && (
                            <div className="ml-2 border-l border-gray-200 dark:border-gray-700 pl-1.5 mb-1">
                              {cat.components.map((comp) => (
                                <div key={comp.id} className="flex items-center gap-1 py-0.5">
                                  <button
                                    onClick={() => {
                                      if (comp.sectionType === 'navbar') {
                                        let blocked = false;
                                        setSections(prev => {
                                          if (prev.some(s => s.type === 'navbar')) {
                                            blocked = true;
                                            return prev;
                                          }
                                          const newSection = { id: generateSectionId('navbar', prev), type: 'navbar' as const, props: { ...comp.defaultProps } };
                                          return [newSection, ...prev];
                                        });
                                        if (blocked) {
                                          toast({ title: 'Navbar già presente', description: 'Puoi avere una sola navbar per pagina', variant: 'destructive' });
                                          return;
                                        }
                                      } else {
                                        setSections(prev => {
                                          const newSection = {
                                            id: generateSectionId(comp.sectionType, prev),
                                            type: comp.sectionType,
                                            props: { ...comp.defaultProps }
                                          };
                                          return insertAfterSection(prev, newSection, selectedSectionId);
                                        });
                                      }
                                      setShowComponentPanel(false);
                                      toast({ title: 'Componente aggiunto', description: `"${comp.name}" aggiunto alla pagina` });
                                    }}
                                    className="flex-1 px-2 py-1 text-left text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-700 group"
                                    title={comp.description}
                                  >
                                    <span className="text-gray-800 dark:text-white text-[11px]">{comp.name}</span>
                                    <p className="text-[9px] text-gray-400 truncate">{comp.description}</p>
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedComponent(comp);
                                      setShowComponentPanel(false);
                                      setChatInput(`Personalizza il componente "${comp.name}" per il mio business.`);
                                      toast({ title: 'Componente selezionato', description: `Scrivi i dettagli del tuo business e l'AI personalizzerà "${comp.name}"` });
                                    }}
                                    className="p-1 text-gray-400 hover:text-emerald-500 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20 flex-shrink-0"
                                    title="Personalizza con AI"
                                  >
                                    <Sparkles className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {showScrapeInput && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 focus-within:ring-2 focus-within:ring-inset focus-within:ring-emerald-500">
                  <Link2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  <input
                    value={scrapeUrl}
                    onChange={(e) => setScrapeUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && scrapeCompetitorUrl()}
                    placeholder="https://competitor.com"
                    className="flex-1 bg-transparent border-none outline-none text-xs text-gray-900 dark:text-white placeholder-gray-400"
                    disabled={scraping}
                  />
                  <button
                    onClick={scrapeCompetitorUrl}
                    disabled={scraping || !scrapeUrl.trim()}
                    className="px-2 py-0.5 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-40 flex items-center gap-1"
                  >
                    {scraping ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                    {scraping ? 'Analisi...' : 'Analizza'}
                  </button>
                  <button onClick={() => { setShowScrapeInput(false); setScrapeUrl(''); }} className="text-gray-400 hover:text-gray-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {scrapedContent && (
                <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-xs">
                  <Globe className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-emerald-700 dark:text-emerald-300">Pagina analizzata: <strong>{scrapedContent.domain}</strong></span>
                  <button onClick={() => setScrapedContent(null)} className="ml-auto text-gray-400 hover:text-gray-600">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {uploading && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-500" />
                  Caricamento immagine...
                </div>
              )}
              <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 rounded-xl px-2 py-1 border border-gray-200 dark:border-gray-700 focus-within:border-sky-400 transition-colors">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                        enableImageGen || showWidgetPanel || showComponentPanel || showScrapeInput || scrapedContent
                          ? 'text-sky-500 bg-sky-100 dark:bg-sky-900/30'
                          : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="top" align="start" className="w-52">
                    <DropdownMenuItem
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || generating}
                    >
                      <Paperclip className="h-4 w-4 mr-2" />
                      Carica immagine
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setShowScrapeInput(!showScrapeInput)}
                    >
                      <Link2 className={`h-4 w-4 mr-2 ${showScrapeInput || scrapedContent ? 'text-emerald-500' : ''}`} />
                      Analizza URL competitor
                      {(showScrapeInput || scrapedContent) && <Check className="h-3.5 w-3.5 ml-auto text-emerald-500" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setEnableImageGen(!enableImageGen)}
                    >
                      <Wand2 className={`h-4 w-4 mr-2 ${enableImageGen ? 'text-purple-500' : ''}`} />
                      Genera immagini AI
                      {enableImageGen && <Check className="h-3.5 w-3.5 ml-auto text-purple-500" />}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setShowWidgetPanel(!showWidgetPanel)}
                      disabled={generating}
                    >
                      <Puzzle className={`h-4 w-4 mr-2 ${showWidgetPanel ? 'text-purple-500' : ''}`} />
                      Inserisci widget
                      {showWidgetPanel && <Check className="h-3.5 w-3.5 ml-auto text-purple-500" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => { setShowComponentPanel(!showComponentPanel); if (!showComponentPanel) loadComponentLibrary(); }}
                      disabled={generating}
                    >
                      <LayoutGrid className={`h-4 w-4 mr-2 ${showComponentPanel ? 'text-emerald-500' : ''}`} />
                      Catalogo componenti
                      {showComponentPanel && <Check className="h-3.5 w-3.5 ml-auto text-emerald-500" />}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(file);
                    e.target.value = '';
                  }}
                  disabled={uploading}
                />
                <input
                  data-chat-input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                  placeholder="Descrivi la tua attività e obiettivi..."
                  className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 py-2 px-1"
                  disabled={generating}
                />
                <button
                  onClick={() => sendChat()}
                  disabled={generating || !chatInput.trim()}
                  className="p-1.5 text-white bg-sky-500 hover:bg-sky-600 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  title="Invia"
                >
                  <Send className="h-4 w-4" />
                </button>
                {sections.length === 0 && chatInput.trim() && (
                  <button
                    onClick={() => sendChat(true)}
                    disabled={generating}
                    className="p-1.5 text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    title="Genera subito (salta brainstorming)"
                  >
                    <Sparkles className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {showSectionPanel && sections.length > 0 && (
            <SectionPanel
              sections={sections}
              selectedSectionId={selectedSectionId}
              onSelectSection={setSelectedSectionId}
              onReorder={setSections}
              onDelete={(idx) => {
                if (selectedSectionId === sections[idx]?.id) setSelectedSectionId(null);
                setSections(prev => prev.filter((_, i) => i !== idx));
              }}
              onMoveUp={(idx) => {
                if (idx <= 0) return;
                setSections(prev => {
                  const n = [...prev];
                  const a = n[idx - 1];
                  const b = n[idx];
                  if (a === undefined || b === undefined) return prev;
                  n[idx - 1] = b;
                  n[idx] = a;
                  return n;
                });
              }}
              onMoveDown={(idx) => {
                if (idx >= sections.length - 1) return;
                setSections(prev => {
                  const n = [...prev];
                  const a = n[idx];
                  const b = n[idx + 1];
                  if (a === undefined || b === undefined) return prev;
                  n[idx] = b;
                  n[idx + 1] = a;
                  return n;
                });
              }}
              onClose={() => setShowSectionPanel(false)}
              onEditProperties={(sectionId) => {
                setSelectedSectionId(sectionId);
                setShowPropertiesPanel(true);
              }}
              onConfigureWidget={(sectionId) => {
                openWidgetEditDialog(sectionId);
              }}
              onReplaceImage={(sectionId) => {
                const section = sections.find(s => s.id === sectionId);
                if (section) {
                  const target = section.props?.backgroundImage
                    ? `${sectionId}::backgroundImage::`
                    : `${sectionId}::items_image::0`;
                  setStockImagePreselectedTarget(target);
                  setShowStockImageModal(true);
                }
              }}
            />
          )}

          <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-950 relative z-0">
            {sections.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
                <Layout className="h-16 w-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium">Preview del Progetto</p>
                <p className="text-sm mt-1">Usa la chat per generare la tua pagina</p>
              </div>
            ) : (
              <PreviewIframe
                sections={sections}
                settings={{ ...(editingPage?.settings || {}), custom_head_code: customHeadCode, custom_body_code: customBodyCode, custom_css: customCss }}
                title={editingPage?.title || 'Preview'}
                selectedSectionId={selectedSectionId}
                onSectionSelect={(id) => {
                  setSelectedSectionId(id);
                  if (id) setShowPropertiesPanel(true);
                }}
                previewDevice={previewDevice}
                setPreviewDevice={setPreviewDevice}
              />
            )}
          </div>

          {showPropertiesPanel && selectedSectionId && (() => {
            const sec = sections.find(s => s.id === selectedSectionId);
            if (!sec) return null;
            return (
              <PropertiesPanel
                section={sec}
                onUpdate={(sectionId, newProps) => {
                  setSections(prev => prev.map(s => s.id === sectionId ? { ...s, props: newProps } : s));
                }}
                onClose={() => setShowPropertiesPanel(false)}
                linkablePages={linkablePages}
                currentPageId={editingPage?.id}
                currentSections={sections}
                onOpenStockImage={(sectionId) => {
                  setStockImagePreselectedTarget(`${sectionId}::backgroundImage::`);
                  setShowStockImageModal(true);
                }}
                appointmentForms={(availableWidgets.appointmentForms || []).map((f) => ({ id: f.id, name: f.name ?? '', slug: f.slug ?? '' }))}
              />
            );
          })()}

          {showPageSettings && editingPage && (
            <div className="w-[280px] min-w-[280px] border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-y-auto p-4 space-y-5">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Settings className="h-4 w-4 text-purple-500" /> Impostazioni Pagina
                </h4>
                <button onClick={() => setShowPageSettings(false)} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Tema Colori</Label>
                <Select value={editingPage.settings?.colorMode || 'light'} onValueChange={(v) => updatePageSettings('colorMode', v)}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light"><span className="flex items-center gap-2"><Sun className="h-3.5 w-3.5" /> Light</span></SelectItem>
                    <SelectItem value="dark"><span className="flex items-center gap-2"><Moon className="h-3.5 w-3.5" /> Dark</span></SelectItem>
                    <SelectItem value="auto"><span className="flex items-center gap-2"><MonitorIcon className="h-3.5 w-3.5" /> Auto (sistema)</span></SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400">Modalità colori della pagina pubblicata</p>
              </div>

              {fontPairs.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Font Pair</Label>
                  <Select value={(editingPage.settings?.fontPair as string) || 'space-inter'} onValueChange={(v) => updatePageSettings('fontPair', v)}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {fontPairs.map((fp) => (
                        <SelectItem key={fp.id} value={fp.id}>
                          <span className="flex flex-col">
                            <span>{fp.name}</span>
                            <span className="text-xs text-gray-400">{fp.displayFont} + {fp.bodyFont}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400">Combinazione di font per titoli e testo</p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Tipo Attività (SEO)</Label>
                <Select value={editingPage.settings?.businessType || 'LocalBusiness'} onValueChange={(v) => updatePageSettings('businessType', v)}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LocalBusiness">Attività Locale</SelectItem>
                    <SelectItem value="LegalService">Studio Legale</SelectItem>
                    <SelectItem value="Product">Prodotto</SelectItem>
                    <SelectItem value="Restaurant">Ristorante</SelectItem>
                    <SelectItem value="MedicalBusiness">Studio Medico</SelectItem>
                    <SelectItem value="RealEstateAgent">Immobiliare</SelectItem>
                    <SelectItem value="ProfessionalService">Servizio Professionale</SelectItem>
                    <SelectItem value="Organization">Organizzazione</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400">Per i dati strutturati JSON-LD</p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Lingua pagina (HTML lang)</Label>
                <Input
                  value={editingPage.settings?.language || ''}
                  onChange={(e) => updatePageSettings('language', e.target.value)}
                  placeholder="it"
                  className="h-8 text-sm"
                  maxLength={10}
                />
                <p className="text-xs text-gray-400">Codice lingua ISO (es. it, en, de). Default: it</p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Favicon URL</Label>
                <Input
                  value={editingPage.settings?.faviconUrl || ''}
                  onChange={(e) => updatePageSettings('faviconUrl', e.target.value)}
                  placeholder="https://example.com/favicon.ico"
                  className="h-8 text-sm"
                />
                <p className="text-xs text-gray-400">URL dell'icona del sito (favicon)</p>
              </div>

              <div className="space-y-2">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Nascondi dai motori di ricerca</Label>
                    <p className="text-xs text-gray-400">Aggiunge noindex alla pagina</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={editingPage.settings?.robotsNoIndex || false}
                    onChange={(e) => updatePageSettings('robotsNoIndex', e.target.checked)}
                    className="h-4 w-4 text-purple-500 rounded border-gray-300"
                  />
                </label>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">SEO Avanzato</p>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Titolo SEO</Label>
                  <Input
                    value={editingPage.seo_meta?.title || ''}
                    onChange={(e) => updateSeoMeta('title', e.target.value)}
                    placeholder={editingPage.title}
                    className="h-8 text-sm"
                    maxLength={70}
                  />
                  <p className={`text-xs ${(editingPage.seo_meta?.title || editingPage.title || '').length > 60 ? 'text-amber-500' : 'text-gray-400'}`}>
                    {(editingPage.seo_meta?.title || editingPage.title || '').length}/60 caratteri
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Meta Description</Label>
                  <textarea
                    value={editingPage.seo_meta?.description || ''}
                    onChange={(e) => updateSeoMeta('description', e.target.value)}
                    placeholder={editingPage.description || ''}
                    className="w-full h-16 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 resize-none"
                    maxLength={200}
                  />
                  <p className={`text-xs ${(editingPage.seo_meta?.description || editingPage.description || '').length > 160 ? 'text-amber-500' : 'text-gray-400'}`}>
                    {(editingPage.seo_meta?.description || editingPage.description || '').length}/160 caratteri
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Focus Keyword</Label>
                  <Input
                    value={editingPage.seo_meta?.focusKeyword || ''}
                    onChange={(e) => updateSeoMeta('focusKeyword', e.target.value)}
                    placeholder="es. ristorante milano"
                    className="h-8 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Canonical URL (override)</Label>
                  <Input
                    value={editingPage.seo_meta?.customCanonical || ''}
                    onChange={(e) => updateSeoMeta('customCanonical', e.target.value)}
                    placeholder={`${publicBaseUrl}/${editingPage.slug}`}
                    className="h-8 text-sm"
                  />
                  <p className="text-xs text-gray-400">Lascia vuoto per usare l'URL automatico</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">OG Locale</Label>
                  <Input
                    value={editingPage.seo_meta?.ogLocale || ''}
                    onChange={(e) => updateSeoMeta('ogLocale', e.target.value)}
                    placeholder="it_IT"
                    className="h-8 text-sm"
                  />
                </div>

                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Anteprima Google</p>
                  <div className="space-y-0.5">
                    <p className="text-sm text-blue-700 dark:text-blue-400 font-medium truncate" style={{ maxWidth: '100%' }}>
                      {(editingPage.seo_meta?.title || editingPage.title || 'Titolo pagina').substring(0, 60)}
                      {(editingPage.seo_meta?.title || editingPage.title || '').length > 60 ? '...' : ''}
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-500 truncate">
                      {editingPage.seo_meta?.customCanonical || `${publicBaseUrl}/${editingPage.slug}`}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                      {(editingPage.seo_meta?.description || editingPage.description || 'Descrizione della pagina...').substring(0, 160)}
                      {(editingPage.seo_meta?.description || editingPage.description || '').length > 160 ? '...' : ''}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">SEO Checklist</p>
                  <div className="space-y-1">
                    {(() => {
                      const fk = (editingPage.seo_meta?.focusKeyword || '').toLowerCase().trim();
                      const seoTitle = (editingPage.seo_meta?.title || editingPage.title || '').toLowerCase();
                      const seoDesc = (editingPage.seo_meta?.description || editingPage.description || '').toLowerCase();
                      const kwInTitle = fk ? seoTitle.includes(fk) : false;
                      const kwInDesc = fk ? seoDesc.includes(fk) : false;
                      return [
                        { ok: (editingPage.seo_meta?.title || editingPage.title || '').length > 0 && (editingPage.seo_meta?.title || editingPage.title || '').length <= 60, label: 'Titolo (1-60 car.)' },
                        { ok: (editingPage.seo_meta?.description || editingPage.description || '').length >= 50 && (editingPage.seo_meta?.description || editingPage.description || '').length <= 160, label: 'Description (50-160 car.)' },
                        { ok: !!fk, label: 'Focus keyword definita' },
                        { ok: kwInTitle, label: 'Keyword nel titolo' },
                        { ok: kwInDesc, label: 'Keyword nella description' },
                        { ok: !!(editingPage.seo_meta?.ogImage), label: 'OG Image impostata' },
                        { ok: sections.some(s => s.type === 'hero' && s.props?.backgroundImage), label: 'Hero con immagine' },
                      ];
                    })().map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={item.ok ? 'text-green-500' : 'text-gray-300'}>{item.ok ? '✓' : '○'}</span>
                        <span className={item.ok ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Dati Attività (JSON-LD)</p>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Indirizzo</Label>
                  <Input
                    value={editingPage.settings?.businessAddress || ''}
                    onChange={(e) => updatePageSettings('businessAddress', e.target.value)}
                    placeholder="Via Roma 1, Milano"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Telefono</Label>
                  <Input
                    value={editingPage.settings?.businessPhone || ''}
                    onChange={(e) => updatePageSettings('businessPhone', e.target.value)}
                    placeholder="+39 02 1234567"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400">Orari Apertura</Label>
                  <Input
                    value={editingPage.settings?.businessHours || ''}
                    onChange={(e) => updatePageSettings('businessHours', e.target.value)}
                    placeholder="Mo-Fr 09:00-18:00"
                    className="h-8 text-sm"
                  />
                  <p className="text-xs text-gray-400">Formato: Mo-Fr 09:00-18:00, Sa 10:00-14:00</p>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Info</p>
                <div className="space-y-1 text-xs text-gray-500">
                  <p>Sitemap: /{tenantSlug}/sitemap.xml</p>
                  <p>Robots: /{tenantSlug}/robots.txt</p>
                </div>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      <StockImageModal
        open={showStockImageModal}
        onOpenChange={(open) => {
          setShowStockImageModal(open);
          if (!open) setStockImagePreselectedTarget(undefined);
        }}
        sections={sections}
        onImageSelect={handleStockImageSelect}
        initialTarget={stockImagePreselectedTarget}
      />

      <Dialog open={showSitemapModal} onOpenChange={setShowSitemapModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapIcon className="h-5 w-5 text-emerald-500" />
              Sitemap — {editingPage?.site_group_id ? (siteGroups.find(g => g.id === editingPage.site_group_id)?.name || 'Progetto') : 'Progetto'}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            if (!editingPage?.site_group_id) return null;
            const projectPages = pages.filter(p => p.site_group_id === editingPage.site_group_id);
            const groupName = siteGroups.find(g => g.id === editingPage.site_group_id)?.name;
            const rootPages = projectPages.filter(p => !p.parent_id);
            const childPages = projectPages.filter(p => p.parent_id);
            const publishedCount = projectPages.filter(p => p.status === 'published').length;
            const draftCount = projectPages.filter(p => p.status === 'draft').length;
            const archivedCount = projectPages.filter(p => p.status === 'archived').length;
            const lastUpdate = projectPages.length > 0
              ? new Date(Math.max(...projectPages.map(p => new Date(p.updated_at).getTime()))).toLocaleString('it-IT')
              : '—';

            const statusBadge = (status: string) => {
              const map: Record<string, { bg: string; text: string; label: string }> = {
                published: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Pubblicata' },
                draft: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', label: 'Bozza' },
                archived: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', label: 'Archiviata' },
              };
              const s = map[status] || map.draft;
              return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${s!.bg!} ${s!.text!}`}>{s!.label!}</span>;
            };

            const renderPageNode = (page: LandingPage, isChild = false) => {
              const isCurrent = page.id === editingPage.id;
              const pageChildren = childPages.filter(c => c.parent_id === page.id);
              const seoTitle = page.seo_meta?.title;
              const seoDesc = page.seo_meta?.description;
              const sectionCount = Array.isArray(page.sections) ? page.sections.length : 0;
              return (
                <div key={page.id}>
                  <div
                    onClick={() => { if (!isCurrent) { openEditor(page); setShowSitemapModal(false); } }}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                      isCurrent
                        ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-200 dark:ring-emerald-800'
                        : 'border-gray-200 dark:border-gray-700 hover:border-sky-300 dark:hover:border-sky-700 hover:bg-sky-50/50 dark:hover:bg-sky-900/10 cursor-pointer'
                    } ${isChild ? 'ml-8' : ''}`}
                  >
                    {isChild && (
                      <div className="absolute -ml-11 mt-3 w-5 border-b-2 border-l-2 border-gray-300 dark:border-gray-600 h-4 rounded-bl" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{page.title}</span>
                        {statusBadge(page.status)}
                        {isCurrent && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400">Corrente</span>}
                        {!page.parent_id && pageChildren.length > 0 && (
                          <span className="text-[10px] text-gray-400">{pageChildren.length} sotto-pagine</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Link2 className="h-3 w-3" />
                          <code className="text-[11px] bg-gray-100 dark:bg-gray-800 px-1 rounded">/{tenantSlug}/site/{page.slug}</code>
                        </span>
                        <span className="flex items-center gap-1">
                          <Layers className="h-3 w-3" /> {sectionCount} sezioni
                        </span>
                        {page.published_at && (
                          <span className="flex items-center gap-1">
                            <Globe className="h-3 w-3" /> {new Date(page.published_at).toLocaleDateString('it-IT')}
                          </span>
                        )}
                      </div>
                      {(seoTitle || seoDesc) && (
                        <div className="mt-1.5 p-2 bg-gray-50 dark:bg-gray-800/50 rounded border border-gray-100 dark:border-gray-700/50">
                          {seoTitle && <p className="text-[11px] font-medium text-gray-700 dark:text-gray-300 truncate"><Search className="h-3 w-3 inline mr-1 text-gray-400" />{seoTitle}</p>}
                          {seoDesc && <p className="text-[10px] text-gray-400 truncate mt-0.5">{seoDesc}</p>}
                        </div>
                      )}
                      <p className="text-[10px] text-gray-400 mt-1">Aggiornata: {new Date(page.updated_at).toLocaleString('it-IT')}</p>
                    </div>
                  </div>
                  {pageChildren.length > 0 && (
                    <div className="relative mt-1 space-y-1">
                      {pageChildren.map(child => renderPageNode(child, true))}
                    </div>
                  )}
                </div>
              );
            };

            return (
              <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center p-2.5 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
                    <p className="text-lg font-bold text-sky-600 dark:text-sky-400">{projectPages.length}</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Totale Pagine</p>
                  </div>
                  <div className="text-center p-2.5 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">{publishedCount}</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Pubblicate</p>
                  </div>
                  <div className="text-center p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{draftCount}</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Bozze</p>
                  </div>
                  <div className="text-center p-2.5 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-lg font-bold text-gray-600 dark:text-gray-400">{archivedCount}</p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Archiviate</p>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 px-1">
                  <span className="font-medium">{groupName || rootPages[0]?.title || 'Progetto'}</span>
                  <span>Ultimo aggiornamento: {lastUpdate}</span>
                </div>

                <div className="space-y-1.5">
                  {rootPages.map(page => renderPageNode(page))}
                  {childPages.filter(c => !rootPages.some(r => r.id === c.parent_id) && !childPages.some(p2 => p2.id === c.parent_id)).map(orphan => renderPageNode(orphan))}
                </div>

                {projectPages.length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">URL Pubblici</p>
                    <div className="space-y-1">
                      <p className="text-[11px] text-gray-400 flex items-center gap-1"><Globe className="h-3 w-3" /> Sitemap: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">/{tenantSlug}/sitemap.xml</code></p>
                      <p className="text-[11px] text-gray-400 flex items-center gap-1"><FileText className="h-3 w-3" /> Robots: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">/{tenantSlug}/robots.txt</code></p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <WidgetConfigDialog
        widgetConfigDialog={widgetConfigDialog}
        setWidgetConfigDialog={setWidgetConfigDialog}
        widgetConfig={widgetConfig}
        setWidgetConfig={setWidgetConfig}
        setSections={setSections}
        selectedSectionId={selectedSectionId}
      />

      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className={newPageStep === 'templates' ? 'max-w-4xl' : 'max-w-md'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {newPageStep === 'templates' ? (
                <><Layout className="h-5 w-5 text-sky-500" /> Scegli come iniziare</>
              ) : (
                <><Sparkles className="h-5 w-5 text-sky-500" /> Dettagli Pagina</>
              )}
            </DialogTitle>
          </DialogHeader>

          {newPageStep === 'templates' ? (
            <div className="py-2">
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                  <button
                    onClick={() => { setSelectedTemplate('scratch'); setNewPageStep('details'); }}
                    className="group relative flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-sky-400 hover:bg-sky-50/50 dark:hover:bg-sky-900/10 transition-all duration-200 text-center min-h-[160px]"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm">Da Zero con AI</p>
                    <p className="text-xs text-gray-400 mt-1">L'AI crea la pagina in base alla tua descrizione</p>
                  </button>

                  {[...((newParentId || newGroupId) ? childTemplates : []), ...templates].map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => { setSelectedTemplate(tpl.id); setNewPageStep('details'); }}
                      className="group relative flex flex-col items-center p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-sky-400 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-center min-h-[160px]"
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 text-2xl group-hover:scale-110 transition-transform"
                        style={{ backgroundColor: `${tpl.color}15` }}
                      >
                        {tpl.icon}
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm">{tpl.name}</p>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{tpl.description}</p>
                      <div className="absolute top-2 right-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tpl.color }} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {selectedTemplate && selectedTemplate !== 'scratch' && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
                  <span className="text-xl">{templates.find(t => t.id === selectedTemplate)?.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Template: {templates.find(t => t.id === selectedTemplate)?.name}</p>
                    <p className="text-xs text-gray-500">Sezioni professionali pre-configurate, personalizzabili con AI</p>
                  </div>
                  <button onClick={() => setNewPageStep('templates')} className="ml-auto text-xs text-sky-500 hover:text-sky-600 font-medium">Cambia</button>
                </div>
              )}
              {selectedTemplate === 'scratch' && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Creazione da Zero</p>
                    <p className="text-xs text-gray-500">L'AI genererà la pagina in base alla tua descrizione</p>
                  </div>
                  <button onClick={() => setNewPageStep('templates')} className="ml-auto text-xs text-sky-500 hover:text-sky-600 font-medium">Cambia</button>
                </div>
              )}
              {newGroupId && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
                  <Layers className="h-4 w-4 text-sky-500" />
                  <span className="text-xs font-medium text-sky-700 dark:text-sky-400">La pagina verrà aggiunta al progetto corrente</span>
                </div>
              )}
              {newGroupId && (() => {
                const parentCandidates = linkablePages.filter(lp => !lp.parent_id);
                if (parentCandidates.length === 0) return null;
                return (
                  <div className="space-y-2">
                    <Label>Pagina padre (opzionale)</Label>
                    <Select value={newParentId || '__none__'} onValueChange={(v) => {
                      const pid = v === '__none__' ? null : v;
                      setNewParentId(pid);
                      if (pid) {
                        const parent = linkablePages.find(lp => lp.id === pid);
                        if (parent && newTitle) {
                          const base = newSlug ? newSlug.split('/').pop() || '' : newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                          setNewSlug(base);
                        }
                      } else {
                        const base = newSlug ? newSlug.split('/').pop() || newSlug : '';
                        setNewSlug(base);
                      }
                    }}>
                      <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Nessuna (pagina radice)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Nessuna (pagina radice)</SelectItem>
                        {parentCandidates.map(pc => (
                          <SelectItem key={pc.id} value={pc.id}>{pc.title} <span className="text-gray-400 ml-1">/{pc.slug}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">Se selezioni un padre, la pagina diventa una sotto-pagina gerarchica</p>
                  </div>
                );
              })()}
              <div className="space-y-2">
                <Label>Titolo</Label>
                <Input
                  value={newTitle}
                  onChange={(e) => {
                    setNewTitle(e.target.value);
                    const base = e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                    if (!newSlug || newSlug === newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')) {
                      setNewSlug(base);
                    }
                  }}
                  placeholder="Es: Chi Siamo, Servizi, Contatti..."
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label>Slug URL</Label>
                {(() => {
                  const parentPage = newParentId ? linkablePages.find(lp => lp.id === newParentId) : null;
                  const parentPrefix = parentPage ? `/${parentPage.slug}/` : '';
                  const fullSlug = parentPage ? `${parentPage.slug}/${newSlug || 'slug-pagina'}` : (newSlug || 'slug-pagina');
                  return (
                    <>
                      <div className="flex items-center">
                        {parentPrefix && (
                          <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1.5 rounded-l border border-r-0 border-gray-300 dark:border-gray-600 whitespace-nowrap">
                            {parentPrefix}
                          </span>
                        )}
                        <Input
                          value={newSlug}
                          onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                          placeholder="chi-siamo"
                          className={parentPrefix ? 'rounded-l-none' : ''}
                        />
                      </div>
                      <p className="text-xs text-gray-500">La pagina sarà accessibile a /{tenantSlug}/site/{fullSlug}</p>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          <DialogFooter>
            {newPageStep === 'details' && (
              <Button variant="outline" onClick={() => setNewPageStep('templates')}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Annulla</Button>
            {newPageStep === 'details' && (
              <Button onClick={createPage} disabled={!newTitle.trim()} className="bg-sky-500 hover:bg-sky-600 text-white">
                <Sparkles className="h-4 w-4 mr-2" /> {selectedTemplate === 'scratch' ? 'Crea da Zero' : 'Crea con Template'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {renderSettingsDialog()}
      </>
    );
  }

  if (showAnalytics && (analyticsPageId || analyticsGroupId)) {
    const currentPage = analyticsPageId ? pages.find(p => p.id === analyticsPageId) : null;
    return (
      <div className="space-y-6">
        {loadingAnalytics ? (
          <div className="flex items-center justify-center py-16">
            <Button variant="ghost" size="sm" onClick={() => { setShowAnalytics(false); setAnalyticsPageId(null); setAnalyticsGroupId(null); setAnalyticsData(null); setAnalyticsMode('page'); }} className="absolute left-4 top-4">
              <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
            </Button>
            <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
          </div>
        ) : (
          <LandingAnalyticsDashboard
            analyticsData={analyticsData}
            // `pages` è consumato opaco dalla dashboard (righe aggregate SQL o
            // elenco pagine): entrambe le shape sono record dinamici.
            pages={(analyticsData?.pages ?? pages) as Record<string, unknown>[]}
            mode={analyticsMode}
            pageTitle={currentPage?.title}
            onBack={() => { setShowAnalytics(false); setAnalyticsPageId(null); setAnalyticsGroupId(null); setAnalyticsData(null); setAnalyticsMode('page'); setAnalyticsCustomHeadCode(''); }}
            onPageDrillDown={analyticsMode === 'project' ? (pageId: string) => {
              setAnalyticsPageId(pageId);
              setAnalyticsMode('page');
              setAnalyticsData(null);
              loadAnalytics(pageId);
            } : undefined}
            customHeadCode={analyticsCustomHeadCode}
            onPeriodChange={(days: number) => {
              if (analyticsMode === 'project' && analyticsGroupId) {
                loadProjectAnalytics(analyticsGroupId, days);
              } else if (analyticsPageId) {
                loadAnalytics(analyticsPageId, days);
              }
            }}
            onNavigateToCode={() => {
              const targetPage = analyticsPageId
                ? pages.find(p => p.id === analyticsPageId)
                : analyticsGroupId
                  ? pages.find(p => p.site_group_id === analyticsGroupId)
                  : pages[0];
              setShowAnalytics(false);
              setAnalyticsPageId(null);
              setAnalyticsGroupId(null);
              setAnalyticsData(null);
              setAnalyticsMode('page');
              setAnalyticsCustomHeadCode('');
              if (targetPage) {
                openEditor(targetPage).then(() => setEditorTab('code'));
              }
            }}
          />
        )}
      </div>
    );
  }

  const renderPageRow = (page: LandingPage, isChild = false) => (
    <TableRow key={page.id}>
      <TableCell className="font-medium text-gray-900 dark:text-white">
        <div className="flex items-center gap-2">
          {isChild && <span className="ml-4 text-gray-300 dark:text-gray-600">└</span>}
          {page.site_group_id && !isChild && <span className="w-1 h-4 rounded-full bg-sky-400 inline-block" />}
          {isChild && <span className="w-1 h-4 rounded-full bg-sky-300 dark:bg-sky-700 inline-block" />}
          {page.title}
        </div>
      </TableCell>
      <TableCell className="text-sm">
        {page.status === 'published' ? (
          <a href={`${publicBaseUrl}/${page.slug}`} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-600 hover:underline flex items-center gap-1">
            /{tenantSlug}/site/{page.slug}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span className="text-gray-400">/{tenantSlug}/site/{page.slug}</span>
        )}
      </TableCell>
      <TableCell>{statusBadge(page.status)}</TableCell>
      <TableCell className="text-center text-sm text-gray-700 dark:text-gray-300 font-medium">{page.analytics?.total_views || 0}</TableCell>
      <TableCell className="text-center text-sm text-sky-600 font-medium">{page.analytics?.views_7d || 0}</TableCell>
      <TableCell className="text-center"><Sparkline data={page.analytics?.sparkline || [0,0,0,0,0,0,0]} /></TableCell>
      <TableCell className="text-sm text-gray-500">{new Date(page.updated_at).toLocaleDateString('it-IT')}</TableCell>
      <TableCell className="text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => openEditor(page)} className="flex items-center gap-2">
              <Edit className="h-4 w-4" /> Modifica
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setAnalyticsPageId(page.id); setAnalyticsGroupId(null); setAnalyticsMode('page'); setShowAnalytics(true); setAnalyticsData(null); loadAnalytics(page.id); }} className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" /> Analytics
            </DropdownMenuItem>
            {page.status === 'published' && (
              <DropdownMenuItem asChild>
                <a href={`${publicBaseUrl}/${page.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sky-600">
                  <ExternalLink className="h-4 w-4" /> Visualizza Sito
                </a>
              </DropdownMenuItem>
            )}
            {page.status !== 'published' && (
              <DropdownMenuItem onClick={() => updateStatus(page.id, 'published')} className="flex items-center gap-2 text-green-600">
                <Globe className="h-4 w-4" /> Pubblica
              </DropdownMenuItem>
            )}
            {page.status === 'published' && (
              <DropdownMenuItem onClick={() => updateStatus(page.id, 'draft')} className="flex items-center gap-2 text-yellow-600">
                <FileText className="h-4 w-4" /> Sospendi
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => updateStatus(page.id, 'archived')} className="flex items-center gap-2 text-gray-500">
              <Archive className="h-4 w-4" /> Archivia
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { setMovePageId(page.id); setMoveTargetGroup(page.site_group_id || ''); setShowMoveDialog(true); }} className="flex items-center gap-2 text-purple-600">
              <Layers className="h-4 w-4" /> Sposta in Progetto
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => deletePage(page.id)} className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-4 w-4" /> Elimina
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );

  if (view === 'canvas' && canvasGroupId) {
    const canvasPages = pages.filter(p => p.site_group_id === canvasGroupId);
    const parentPages = canvasPages.filter(p => !p.parent_id);
    const childPages = canvasPages.filter(p => p.parent_id);
    const groupName = canvasGroupName || siteGroups.find(g => g.id === canvasGroupId)?.name || 'Progetto';

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setView('list'); setCanvasGroupId(null); setCanvasGroupName(''); loadPages(); }}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
            </Button>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Layers className="h-5 w-5 text-sky-500" />
                {groupName}
              </h3>
              <p className="text-sm text-gray-500">{canvasPages.length} {canvasPages.length === 1 ? 'pagina' : 'pagine'} nel progetto</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const res = await apiClient.get<{ custom_head_code: string; custom_body_code: string }>(`/landing-pages/site-group/${canvasGroupId}/snippets`);
                  setGroupSnippetDialog({ groupId: canvasGroupId, headCode: res.data!.custom_head_code! || '', bodyCode: res.data!.custom_body_code! || '' });
                } catch {
                  setGroupSnippetDialog({ groupId: canvasGroupId, headCode: '', bodyCode: '' });
                }
              }}
            >
              <Code className="h-4 w-4 mr-1" /> Snippet
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAnalyticsGroupId(canvasGroupId);
                setAnalyticsPageId(null);
                setAnalyticsMode('project');
                setShowAnalytics(true);
                setAnalyticsData(null);
                loadProjectAnalytics(canvasGroupId);
              }}
            >
              <BarChart3 className="h-4 w-4 mr-1" /> Analytics
            </Button>
            <Button
              onClick={() => {
                setNewGroupId(canvasGroupId);
                setNewParentId(null);
                setNewProjectMode('standalone');
                setShowNewDialog(true);
                setNewPageStep('templates');
                setSelectedTemplate(null);
                loadTemplates();
                loadLinkablePages(canvasGroupId);
              }}
              className="bg-sky-500 hover:bg-sky-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" /> Aggiungi Pagina
            </Button>
          </div>
        </div>

        {parentPages.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center mb-4">
                <Layout className="h-8 w-8 text-sky-500" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{canvasPages.length === 0 ? 'Il progetto è vuoto' : 'Nessuna pagina principale'}</h4>
              <p className="text-sm text-gray-500 mb-6 max-w-md">Inizia creando la pagina principale del progetto. Potrai poi aggiungere sotto-pagine.</p>
              <Button
                onClick={() => {
                  setNewGroupId(canvasGroupId);
                  setNewParentId(null);
                  setNewProjectMode('standalone');
                  setShowNewDialog(true);
                  setNewPageStep('templates');
                  setSelectedTemplate(null);
                  loadTemplates();
                }}
                className="bg-sky-500 hover:bg-sky-600 text-white"
              >
                <Plus className="h-4 w-4 mr-2" /> Crea Pagina Principale
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titolo Pagina</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-center">Visite</TableHead>
                    <TableHead className="text-center">7 giorni</TableHead>
                    <TableHead className="text-center">Trend</TableHead>
                    <TableHead>Modificata</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parentPages.map(page => (
                    <React.Fragment key={page.id}>
                      {renderPageRow(page)}
                      {childPages.filter(c => c.parent_id === page.id).map(child => renderPageRow(child, true))}
                    </React.Fragment>
                  ))}
                  {childPages.filter(c => !parentPages.some(p => p.id === c.parent_id)).map(child => renderPageRow(child))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className={newPageStep === 'templates' ? 'max-w-4xl' : 'max-w-md'}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {newPageStep === 'templates' ? (
                  <><Layout className="h-5 w-5 text-sky-500" /> Scegli come iniziare</>
                ) : (
                  <><Sparkles className="h-5 w-5 text-sky-500" /> Dettagli Pagina</>
                )}
              </DialogTitle>
            </DialogHeader>

            {newPageStep === 'templates' ? (
              <div className="py-2">
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                    <button
                      onClick={() => { setSelectedTemplate('scratch'); setNewPageStep('details'); }}
                      className="group relative flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-sky-400 hover:bg-sky-50/50 dark:hover:bg-sky-900/10 transition-all duration-200 text-center min-h-[160px]"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <Sparkles className="h-6 w-6 text-white" />
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm">Da Zero con AI</p>
                      <p className="text-xs text-gray-400 mt-1">L'AI crea la pagina in base alla tua descrizione</p>
                    </button>

                    {[...((newParentId || newGroupId) ? childTemplates : []), ...templates].map((tpl) => (
                      <button
                        key={tpl.id}
                        onClick={() => { setSelectedTemplate(tpl.id); setNewPageStep('details'); }}
                        className="group relative flex flex-col items-center p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-sky-400 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-center min-h-[160px]"
                      >
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 text-2xl group-hover:scale-110 transition-transform"
                          style={{ backgroundColor: `${tpl.color}15` }}
                        >
                          {tpl.icon}
                        </div>
                        <p className="font-semibold text-gray-900 dark:text-white text-sm">{tpl.name}</p>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{tpl.description}</p>
                        <div className="absolute top-2 right-2">
                          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tpl.color }} />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 py-2">
                {selectedTemplate && selectedTemplate !== 'scratch' && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
                    <span className="text-xl">{templates.find(t => t.id === selectedTemplate)?.icon}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Template: {templates.find(t => t.id === selectedTemplate)?.name}</p>
                      <p className="text-xs text-gray-500">Sezioni professionali pre-configurate, personalizzabili con AI</p>
                    </div>
                    <button onClick={() => setNewPageStep('templates')} className="ml-auto text-xs text-sky-500 hover:text-sky-600 font-medium">Cambia</button>
                  </div>
                )}
                {selectedTemplate === 'scratch' && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                    <Sparkles className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Creazione da Zero</p>
                      <p className="text-xs text-gray-500">L'AI genererà la pagina in base alla tua descrizione</p>
                    </div>
                    <button onClick={() => setNewPageStep('templates')} className="ml-auto text-xs text-sky-500 hover:text-sky-600 font-medium">Cambia</button>
                  </div>
                )}
                {newGroupId && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
                    <Layers className="h-4 w-4 text-sky-500" />
                    <span className="text-xs font-medium text-sky-700 dark:text-sky-400">La pagina verrà aggiunta al progetto: {canvasGroupName || siteGroups.find(g => g.id === newGroupId)?.name || 'Progetto'}</span>
                  </div>
                )}
                {newGroupId && (() => {
                  const parentCandidates = linkablePages.filter(lp => !lp.parent_id);
                  if (parentCandidates.length === 0) return null;
                  return (
                    <div className="space-y-2">
                      <Label>Pagina padre (opzionale)</Label>
                      <Select value={newParentId || '__none__'} onValueChange={(v) => {
                        const pid = v === '__none__' ? null : v;
                        setNewParentId(pid);
                        if (pid) {
                          const parent = linkablePages.find(lp => lp.id === pid);
                          if (parent && newTitle) {
                            const base = newSlug ? newSlug.split('/').pop() || '' : newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                            setNewSlug(base);
                          }
                        } else {
                          const base = newSlug ? newSlug.split('/').pop() || newSlug : '';
                          setNewSlug(base);
                        }
                      }}>
                        <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Nessuna (pagina radice)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Nessuna (pagina radice)</SelectItem>
                          {parentCandidates.map(pc => (
                            <SelectItem key={pc.id} value={pc.id}>{pc.title} <span className="text-gray-400 ml-1">/{pc.slug}</span></SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">Se selezioni un padre, la pagina diventa una sotto-pagina gerarchica</p>
                    </div>
                  );
                })()}
                <div className="space-y-2">
                  <Label>Titolo Pagina</Label>
                  <Input
                    value={newTitle}
                    onChange={(e) => {
                      setNewTitle(e.target.value);
                      const base = e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                      if (!newSlug || newSlug === newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')) {
                        setNewSlug(base);
                      }
                    }}
                    placeholder="Es: Home, Chi Siamo, Servizi..."
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug URL</Label>
                  {(() => {
                    const parentPage = newParentId ? linkablePages.find(lp => lp.id === newParentId) : null;
                    const parentPrefix = parentPage ? `/${parentPage.slug}/` : '';
                    const fullSlug = parentPage ? `${parentPage.slug}/${newSlug || 'slug-pagina'}` : (newSlug || 'slug-pagina');
                    return (
                      <>
                        <div className="flex items-center">
                          {parentPrefix && (
                            <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1.5 rounded-l border border-r-0 border-gray-300 dark:border-gray-600 whitespace-nowrap">
                              {parentPrefix}
                            </span>
                          )}
                          <Input
                            value={newSlug}
                            onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                            placeholder="chi-siamo"
                            className={parentPrefix ? 'rounded-l-none' : ''}
                          />
                        </div>
                        <p className="text-xs text-gray-500">La pagina sarà accessibile a /{tenantSlug}/site/{fullSlug}</p>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            <DialogFooter>
              {newPageStep === 'details' && (
                <Button variant="outline" onClick={() => setNewPageStep('templates')}>
                  <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowNewDialog(false)}>Annulla</Button>
              {newPageStep === 'details' && (
                <Button onClick={createPage} disabled={!newTitle.trim()} className="bg-sky-500 hover:bg-sky-600 text-white">
                  <Sparkles className="h-4 w-4 mr-2" /> {selectedTemplate === 'scratch' ? 'Crea da Zero' : 'Crea con Template'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!groupSnippetDialog} onOpenChange={(o) => { if (!o) setGroupSnippetDialog(null); }}>
          {groupSnippetDialog && (
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5 text-sky-500" /> Snippet Progetto
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Head Code</Label>
                  <textarea
                    value={groupSnippetDialog.headCode}
                    onChange={(e) => setGroupSnippetDialog({ ...groupSnippetDialog, headCode: e.target.value })}
                    className="w-full h-28 text-xs font-mono p-2 border rounded-lg bg-gray-50 dark:bg-gray-900 dark:border-gray-700"
                    placeholder="<!-- Google Tag Manager, etc. -->"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Body Code</Label>
                  <textarea
                    value={groupSnippetDialog.bodyCode}
                    onChange={(e) => setGroupSnippetDialog({ ...groupSnippetDialog, bodyCode: e.target.value })}
                    className="w-full h-28 text-xs font-mono p-2 border rounded-lg bg-gray-50 dark:bg-gray-900 dark:border-gray-700"
                    placeholder="<!-- Scripts before </body> -->"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setGroupSnippetDialog(null)}>Annulla</Button>
                <Button
                  disabled={savingGroupSnippet}
                  onClick={async () => {
                    setSavingGroupSnippet(true);
                    try {
                      await apiClient.put(`/landing-pages/site-group/${groupSnippetDialog.groupId}/snippets`, {
                        custom_head_code: groupSnippetDialog.headCode,
                        custom_body_code: groupSnippetDialog.bodyCode
                      });
                      toast({ title: 'Salvato', description: 'Snippet progetto aggiornati' });
                      setGroupSnippetDialog(null);
                    } catch (err) {
                      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
                    } finally {
                      setSavingGroupSnippet(false);
                    }
                  }}
                  className="bg-sky-500 hover:bg-sky-600 text-white"
                >
                  {savingGroupSnippet ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salva'}
                </Button>
              </DialogFooter>
            </DialogContent>
          )}
        </Dialog>

        <Dialog open={showProjectNameDialog} onOpenChange={setShowProjectNameDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-sky-500" /> Nome del Progetto
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Nome Progetto</Label>
                <Input
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Es: Sito Aziendale, E-commerce 2025..."
                  autoFocus
                />
                <p className="text-xs text-gray-500">Il nome del progetto è diverso dal titolo delle singole pagine</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowProjectNameDialog(false); setNewProjectName(''); }}>Annulla</Button>
              <Button
                onClick={createProjectGroup}
                disabled={!newProjectName.trim() || creatingProject}
                className="bg-sky-500 hover:bg-sky-600 text-white"
              >
                {creatingProject ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Layers className="h-4 w-4 mr-2" />}
                Crea Progetto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showInitialChoice} onOpenChange={setShowInitialChoice}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layout className="h-5 w-5 text-sky-500" /> Cosa vuoi creare?
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <button
                onClick={() => {
                  setShowInitialChoice(false);
                  setShowProjectNameDialog(true);
                  setNewProjectName('');
                }}
                className="group flex flex-col items-center p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-sky-400 hover:bg-sky-50/50 dark:hover:bg-sky-900/10 transition-all duration-200 text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Layers className="h-7 w-7 text-white" />
                </div>
                <p className="font-semibold text-gray-900 dark:text-white">Progetto multi-pagina</p>
                <p className="text-xs text-gray-500 mt-1">Sito con più pagine collegate: home, chi siamo, servizi, contatti...</p>
              </button>
              <button
                onClick={() => {
                  setShowInitialChoice(false);
                  setNewGroupId(null);
                  setNewProjectMode('standalone');
                  setShowNewDialog(true);
                  setNewPageStep('templates');
                  setSelectedTemplate(null);
                  loadTemplates();
                }}
                className="group flex flex-col items-center p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-all duration-200 text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <FileText className="h-7 w-7 text-white" />
                </div>
                <p className="font-semibold text-gray-900 dark:text-white">Landing singola</p>
                <p className="text-xs text-gray-500 mt-1">Una singola pagina indipendente: promo, evento, lead generation...</p>
              </button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInitialChoice(false)}>Annulla</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const existingGroups = (() => {
    const groupIds = [...new Set(pages.filter(p => p.site_group_id).map(p => p.site_group_id))];
    const siteGroupMap = new Map(siteGroups.map(g => [g.id, g.name]));
    return groupIds.map(gid => {
      const dbName = siteGroupMap.get(gid!);
      if (dbName) return { id: gid, label: dbName };
      return { id: gid, label: 'Progetto senza nome' };
    });
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Globe className="h-5 w-5 text-sky-500" />
            Web Builder
          </h3>
          <p className="text-sm text-gray-500">Gestisci siti e landing page: crea, modifica e pubblica le tue pagine web</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setShowSettingsDialog(true); loadSystemPrompt(); }}>
            <Settings className="h-4 w-4 mr-2" /> Impostazioni
          </Button>
          <Button onClick={() => setShowInitialChoice(true)} className="bg-sky-500 hover:bg-sky-600 text-white">
            <Plus className="h-4 w-4 mr-2" /> Nuovo Progetto
          </Button>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca progetto..."
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Filtra stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="draft">Bozza</SelectItem>
            <SelectItem value="published">Pubblicata</SelectItem>
            <SelectItem value="archived">Archiviata</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome Pagina</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-center">Visite</TableHead>
                <TableHead className="text-center">7 giorni</TableHead>
                <TableHead className="text-center">Trend</TableHead>
                <TableHead>Modificata</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-sky-500" />
                  </TableCell>
                </TableRow>
              ) : pages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    Nessun progetto trovato
                  </TableCell>
                </TableRow>
              ) : (() => {
                const groups: Record<string, LandingPage[]> = {};
                const standalone: LandingPage[] = [];
                pages.forEach(page => {
                  if (page.site_group_id) {
                    if (!groups[page.site_group_id]) groups[page.site_group_id] = [];
                    groups[page.site_group_id]!.push(page)!;
                  } else {
                    standalone.push(page);
                  }
                });
                const rows: React.ReactNode[] = [];
                Object.entries(groups).forEach(([groupId, groupPages]) => {
                  rows.push(
                    <TableRow key={`group-${groupId}`}>
                      <TableCell colSpan={8} className="bg-sky-50 dark:bg-sky-900/10 py-1.5 px-4">
                        <div className="flex items-center gap-2">
                          <Layers className="h-3.5 w-3.5 text-sky-500" />
                          <button
                            onClick={() => {
                              const gName = siteGroups.find(g => g.id === groupId)?.name || 'Progetto senza nome';
                              setCanvasGroupId(groupId);
                              setCanvasGroupName(gName);
                              setView('canvas');
                            }}
                            className="text-xs font-semibold text-sky-700 dark:text-sky-400 hover:underline cursor-pointer"
                          >
                            Progetto: {siteGroups.find(g => g.id === groupId)?.name || 'Progetto senza nome'}
                          </button>
                          <Badge variant="outline" className="text-[10px] py-0 px-1.5">{groupPages.length} pagine</Badge>
                          <button
                            onClick={async () => {
                              try {
                                const res = await apiClient.get<{ custom_head_code: string; custom_body_code: string }>(`/landing-pages/site-group/${groupId}/snippets`);
                                setGroupSnippetDialog({ groupId, headCode: res.data!.custom_head_code! || '', bodyCode: res.data!.custom_body_code! || '' });
                              } catch {
                                setGroupSnippetDialog({ groupId, headCode: '', bodyCode: '' });
                              }
                            }}
                            className="text-[10px] px-2 py-0.5 rounded bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 hover:bg-sky-200 dark:hover:bg-sky-900/50 transition-colors flex items-center gap-1"
                          >
                            <Code className="h-3 w-3" /> Snippet Progetto
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="ml-auto h-6 text-xs text-sky-600 hover:text-sky-700"
                            onClick={() => {
                              setAnalyticsGroupId(groupId);
                              setAnalyticsPageId(null);
                              setAnalyticsMode('project');
                              setShowAnalytics(true);
                              setAnalyticsData(null);
                              loadProjectAnalytics(groupId);
                            }}
                          >
                            <BarChart3 className="h-3.5 w-3.5 mr-1" /> Analytics Progetto
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                  const parents = groupPages.filter(p => !p.parent_id);
                  const children = groupPages.filter(p => p.parent_id);
                  parents.forEach(page => {
                    rows.push(renderPageRow(page));
                    children.filter(c => c.parent_id === page.id).forEach(child => rows.push(renderPageRow(child, true)));
                  });
                  children.filter(c => !parents.some(p => p.id === c.parent_id)).forEach(child => rows.push(renderPageRow(child)));
                });
                if (standalone.length > 0 && Object.keys(groups).length > 0) {
                  rows.push(
                    <TableRow key="standalone-header">
                      <TableCell colSpan={8} className="bg-gray-50 dark:bg-gray-800/50 py-1.5 px-4">
                        <span className="text-xs font-semibold text-gray-500">Pagine Standalone</span>
                      </TableCell>
                    </TableRow>
                  );
                }
                standalone.forEach(page => rows.push(renderPageRow(page)));
                return rows;
              })()}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className={newPageStep === 'templates' ? 'max-w-4xl' : 'max-w-md'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {newPageStep === 'templates' ? (
                <><Layout className="h-5 w-5 text-sky-500" /> Scegli come iniziare</>
              ) : (
                <><Sparkles className="h-5 w-5 text-sky-500" /> Dettagli Progetto</>
              )}
            </DialogTitle>
          </DialogHeader>

          {newPageStep === 'templates' ? (
            <div className="py-2">
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                  <button
                    onClick={() => { setSelectedTemplate('scratch'); setNewPageStep('details'); }}
                    className="group relative flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-sky-400 hover:bg-sky-50/50 dark:hover:bg-sky-900/10 transition-all duration-200 text-center min-h-[160px]"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm">Da Zero con AI</p>
                    <p className="text-xs text-gray-400 mt-1">L'AI crea la pagina in base alla tua descrizione</p>
                  </button>

                  {[...((newParentId || newGroupId) ? childTemplates : []), ...templates].map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => { setSelectedTemplate(tpl.id); setNewPageStep('details'); }}
                      className="group relative flex flex-col items-center p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-sky-400 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-center min-h-[160px]"
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 text-2xl group-hover:scale-110 transition-transform"
                        style={{ backgroundColor: `${tpl.color}15` }}
                      >
                        {tpl.icon}
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm">{tpl.name}</p>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{tpl.description}</p>
                      <div className="absolute top-2 right-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tpl.color }} />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {selectedTemplate && selectedTemplate !== 'scratch' && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
                  <span className="text-xl">{templates.find(t => t.id === selectedTemplate)?.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Template: {templates.find(t => t.id === selectedTemplate)?.name}</p>
                    <p className="text-xs text-gray-500">Sezioni professionali pre-configurate, personalizzabili con AI</p>
                  </div>
                  <button onClick={() => setNewPageStep('templates')} className="ml-auto text-xs text-sky-500 hover:text-sky-600 font-medium">Cambia</button>
                </div>
              )}
              {selectedTemplate === 'scratch' && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Creazione da Zero</p>
                    <p className="text-xs text-gray-500">L'AI genererà la pagina in base alla tua descrizione</p>
                  </div>
                  <button onClick={() => setNewPageStep('templates')} className="ml-auto text-xs text-sky-500 hover:text-sky-600 font-medium">Cambia</button>
                </div>
              )}
              {!newGroupId && (
                <div className="space-y-2">
                  <Label>Tipo Progetto</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => { setNewProjectMode('standalone'); setCreateTargetGroup(''); }}
                      className={`p-2.5 rounded-lg border text-center text-xs font-medium transition-all ${newProjectMode === 'standalone' ? 'border-sky-400 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400' : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300'}`}
                    >
                      Pagina Singola
                    </button>
                    <button
                      onClick={() => setNewProjectMode('existing')}
                      disabled={existingGroups.length === 0}
                      className={`p-2.5 rounded-lg border text-center text-xs font-medium transition-all ${newProjectMode === 'existing' ? 'border-sky-400 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400' : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300'} ${existingGroups.length === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >
                      Aggiungi a Progetto
                    </button>
                    <button
                      onClick={() => { setNewProjectMode('new'); setCreateTargetGroup(''); }}
                      className={`p-2.5 rounded-lg border text-center text-xs font-medium transition-all ${newProjectMode === 'new' ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400' : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300'}`}
                    >
                      Nuovo Progetto
                    </button>
                  </div>
                  {newProjectMode === 'existing' && existingGroups.length > 0 && (
                    <Select value={createTargetGroup} onValueChange={(v) => { setCreateTargetGroup(v); setNewParentId(null); }}>
                      <SelectTrigger><SelectValue placeholder="Seleziona progetto..." /></SelectTrigger>
                      <SelectContent>
                        {existingGroups.map(g => (
                          <SelectItem key={g.id || ''} value={g.id || ''}>{g.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {newProjectMode === 'existing' && createTargetGroup && (() => {
                    const parentCandidates = pages.filter(p => p.site_group_id === createTargetGroup && !p.parent_id);
                    if (parentCandidates.length === 0) return null;
                    return (
                      <div className="space-y-1">
                        <Label className="text-xs">Pagina padre (opzionale)</Label>
                        <Select value={newParentId || '__none__'} onValueChange={(v) => {
                          const pid = v === '__none__' ? null : v;
                          setNewParentId(pid);
                          if (pid && newTitle) {
                            const parent = parentCandidates.find(p => p.id === pid);
                            if (parent) {
                              const base = newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                              setNewSlug(parent.slug + '/' + base);
                            }
                          }
                        }}>
                          <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Nessuna (pagina radice)" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Nessuna (pagina radice)</SelectItem>
                            {parentCandidates.map(pc => (
                              <SelectItem key={pc.id} value={pc.id}>{pc.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })()}
                </div>
              )}
              {newGroupId && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
                  <Layers className="h-4 w-4 text-sky-500" />
                  <span className="text-xs font-medium text-sky-700 dark:text-sky-400">La pagina verrà aggiunta al progetto corrente</span>
                </div>
              )}
              <div className="space-y-2">
                <Label>Titolo</Label>
                <Input
                  value={newTitle}
                  onChange={(e) => {
                    setNewTitle(e.target.value);
                    if (!newSlug || (newParentId && newSlug.endsWith('/'))) {
                      const base = e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                      if (newParentId) {
                        const parent = pages.find(p => p.id === newParentId) || linkablePages.find(lp => lp.id === newParentId);
                        setNewSlug(parent ? parent.slug + '/' + base : base);
                      } else {
                        setNewSlug(base);
                      }
                    }
                  }}
                  placeholder="Es: Promozione Estiva 2025"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label>Slug URL</Label>
                <Input
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-\/]/g, ''))}
                  placeholder={newParentId ? 'home/sotto-pagina' : 'promozione-estiva-2025'}
                />
                <p className="text-xs text-gray-500">La pagina sarà accessibile a /{tenantSlug}/site/{newSlug || 'slug-pagina'}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            {newPageStep === 'details' && (
              <Button variant="outline" onClick={() => setNewPageStep('templates')}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Annulla</Button>
            {newPageStep === 'details' && (
              <Button onClick={createPage} disabled={!newTitle.trim() || (newProjectMode === 'existing' && !createTargetGroup)} className="bg-sky-500 hover:bg-sky-600 text-white">
                <Sparkles className="h-4 w-4 mr-2" /> {selectedTemplate === 'scratch' ? 'Crea da Zero' : 'Crea con Template'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {renderSettingsDialog()}

      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-purple-500" /> Sposta in Progetto
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Progetto</Label>
              <Select value={moveTargetGroup} onValueChange={setMoveTargetGroup}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {existingGroups.map(g => (
                    <SelectItem key={g.id || ''} value={g.id || ''}>{g.label}</SelectItem>
                  ))}
                  <SelectItem value="__new__">+ Crea nuovo progetto</SelectItem>
                  <SelectSeparator />
                  <SelectItem value="__standalone__" className="text-orange-600 dark:text-orange-400 font-medium">Fuori progetto (standalone)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>Annulla</Button>
            <Button
              onClick={() => {
                if (!movePageId) return;
                if (moveTargetGroup === '__standalone__') moveToGroup(movePageId, null);
                else if (moveTargetGroup === '__new__') moveToGroup(movePageId, crypto.randomUUID());
                else if (moveTargetGroup) moveToGroup(movePageId, moveTargetGroup);
              }}
              disabled={!moveTargetGroup}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              Sposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!groupSnippetDialog} onOpenChange={(open) => { if (!open) setGroupSnippetDialog(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5 text-sky-500" /> Snippet Globali Progetto
            </DialogTitle>
          </DialogHeader>
          {groupSnippetDialog && (
            <div className="space-y-4 py-2">
              <p className="text-xs text-gray-500">Questi snippet vengono iniettati in tutte le pagine del progetto, prima degli snippet individuali di ogni pagina.</p>
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Head Code (globale progetto)</Label>
                <textarea
                  value={groupSnippetDialog.headCode}
                  onChange={(e) => setGroupSnippetDialog(prev => prev ? { ...prev, headCode: e.target.value } : null)}
                  className="w-full min-h-[120px] text-xs font-mono rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-gray-800 dark:text-gray-200 resize-y"
                  placeholder="<!-- Snippet head globale progetto -->"
                  spellCheck={false}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Body Code (globale progetto)</Label>
                <textarea
                  value={groupSnippetDialog.bodyCode}
                  onChange={(e) => setGroupSnippetDialog(prev => prev ? { ...prev, bodyCode: e.target.value } : null)}
                  className="w-full min-h-[120px] text-xs font-mono rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-gray-800 dark:text-gray-200 resize-y"
                  placeholder="<!-- Snippet body globale progetto -->"
                  spellCheck={false}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupSnippetDialog(null)}>Annulla</Button>
            <Button
              disabled={savingGroupSnippet}
              onClick={async () => {
                if (!groupSnippetDialog) return;
                setSavingGroupSnippet(true);
                try {
                  await apiClient.put(`/landing-pages/site-group/${groupSnippetDialog.groupId}/snippets`, {
                    custom_head_code: groupSnippetDialog.headCode,
                    custom_body_code: groupSnippetDialog.bodyCode,
                  });
                  setGroupSnippetDialog(null);
                } catch (err) {
                  console.error('Error saving group snippets:', err);
                } finally {
                  setSavingGroupSnippet(false);
                }
              }}
              className="bg-sky-500 hover:bg-sky-600 text-white"
            >
              {savingGroupSnippet ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInitialChoice} onOpenChange={setShowInitialChoice}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layout className="h-5 w-5 text-sky-500" /> Cosa vuoi creare?
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <button
              onClick={() => {
                setShowInitialChoice(false);
                setShowProjectNameDialog(true);
                setNewProjectName('');
              }}
              className="group flex flex-col items-center p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-sky-400 hover:bg-sky-50/50 dark:hover:bg-sky-900/10 transition-all duration-200 text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Layers className="h-7 w-7 text-white" />
              </div>
              <p className="font-semibold text-gray-900 dark:text-white">Progetto multi-pagina</p>
              <p className="text-xs text-gray-500 mt-1">Sito con più pagine collegate: home, chi siamo, servizi, contatti...</p>
            </button>
            <button
              onClick={() => {
                setShowInitialChoice(false);
                setNewGroupId(null);
                setNewProjectMode('standalone');
                setShowNewDialog(true);
                setNewPageStep('templates');
                setSelectedTemplate(null);
                loadTemplates();
              }}
              className="group flex flex-col items-center p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-all duration-200 text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <FileText className="h-7 w-7 text-white" />
              </div>
              <p className="font-semibold text-gray-900 dark:text-white">Landing singola</p>
              <p className="text-xs text-gray-500 mt-1">Una singola pagina indipendente: promo, evento, lead generation...</p>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInitialChoice(false)}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showProjectNameDialog} onOpenChange={setShowProjectNameDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-sky-500" /> Nome del Progetto
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nome Progetto</Label>
              <Input
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Es: Sito Aziendale, E-commerce 2025..."
                autoFocus
              />
              <p className="text-xs text-gray-500">Il nome del progetto è diverso dal titolo delle singole pagine</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowProjectNameDialog(false); setNewProjectName(''); }}>Annulla</Button>
            <Button
              onClick={createProjectGroup}
              disabled={!newProjectName.trim() || creatingProject}
              className="bg-sky-500 hover:bg-sky-600 text-white"
            >
              {creatingProject ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Layers className="h-4 w-4 mr-2" />}
              Crea Progetto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
